/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Nav, Tab, Badge, Modal, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import budgetService from 'services/budgetService';
import clientService from 'services/clientService';
import { useAuth } from 'contexts/AuthContext';
import AdminFeeModal from '../OrderInsert/AdminFeeModal';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { createColumnHelper } from '@tanstack/react-table';
import useTableExport from '../../hooks/useTableExport';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import BudgetCalculationModal from './BudgetCalculationModal';
import { OrderWizardLayout, OrderWizardProvider } from '../common/OrderWizard';
import HistoryModal from '../logging/HistoryModal';
import { calculateAdvancedBudgetDistribution } from '../../utils/budgetCalculations';


// Componentes optimizados para tooltips
const DailyInfoTooltip = ({ assignment, formatCurrency }) => {
  const distInfo = assignment.distributionInfo;
  
  const tooltipContent = useMemo(() => (
    <Tooltip id={`daily-info-${assignment.id}`} style={{ zIndex: 9999 }}>
      <div style={{ textAlign: 'left', fontSize: '12px' }}>
        <div><strong>Primer día:</strong> {formatCurrency(distInfo?.firstDayAmount || 0)}</div>
        <div><strong>Días Durante:</strong> {formatCurrency(distInfo?.dailyAmountAdjusted || 0)}</div>
        <div><strong>Último día:</strong> {formatCurrency(distInfo?.lastDayAmount || 0)}</div>
      </div>
    </Tooltip>
  ), [assignment.id, distInfo?.firstDayAmount, distInfo?.dailyAmountAdjusted, distInfo?.lastDayAmount, formatCurrency]);
  
  return (
    <div className="text-center">
      <OverlayTrigger
        placement="top"
        delay={{ show: 50, hide: 25 }}
        overlay={tooltipContent}
        trigger={['hover', 'focus']}
        rootClose={true}
      >
        <Button
          variant="link"
          size="sm"
          className="p-0"
          style={{ lineHeight: 1 }}
        >
          <FontAwesomeIcon icon="info-circle" className="text-info" />
        </Button>
      </OverlayTrigger>
    </div>
  );
};

const WeeklyInfoTooltip = ({ assignment, formatCurrency }) => {
  const distInfo = assignment.distributionInfo;
  
  const tooltipContent = useMemo(() => (
    <Tooltip id={`weekly-info-${assignment.id}`} style={{ zIndex: 9999 }}>
      <div style={{ textAlign: 'left', fontSize: '12px' }}>
        <div><strong>Monto Weekly Inicial:</strong> {formatCurrency(distInfo?.weeklyAmountEstimate || 0)}</div>
        <div><strong>Monto Weekly Final:</strong> {formatCurrency(distInfo?.weeklyAmountEstimate || 0)}</div>
        <div><strong>Campaña:</strong> {distInfo?.effectiveDaysPerWeek || 7} días/semana</div>
        <div><strong>Semanas:</strong> {distInfo?.totalWeeks || 'N/A'}</div>
        <div><strong>Días:</strong> {distInfo?.totalDays || 'N/A'}</div>
      </div>
    </Tooltip>
  ), [assignment.id, distInfo?.weeklyAmountEstimate, distInfo?.effectiveDaysPerWeek, distInfo?.totalWeeks, distInfo?.totalDays, formatCurrency]);
  
  return (
    <div className="text-center">
      <OverlayTrigger
        placement="top"
        delay={{ show: 50, hide: 25 }}
        overlay={tooltipContent}
        trigger={['hover', 'focus']}
        rootClose={true}
      >
        <Button
          variant="link"
          size="sm"
          className="p-0"
          style={{ lineHeight: 1 }}
        >
          <FontAwesomeIcon icon="info-circle" className="text-warning" />
        </Button>
      </OverlayTrigger>
    </div>
  );
};

// Componente mejorado para Info Desgloze Diario
const DailyBreakdownTooltip = ({ assignment, formatCurrency }) => {
  const distInfo = assignment.distributionInfo;
  const dateFrom = new Date(assignment.dateFrom);
  const dateTo = new Date(assignment.dateTo);
  
  // Calcular información adicional
  const totalDays = distInfo?.totalDays || 0;
  const effectiveDaysPerWeek = distInfo?.effectiveDaysPerWeek || 7;
  const effectiveDays = Math.ceil((totalDays * effectiveDaysPerWeek) / 7);
  const nonWorkingDays = totalDays - effectiveDays;
  
  const tooltipContent = useMemo(() => (
    <Tooltip id={`breakdown-${assignment.id}`} style={{ zIndex: 9999 }}>
      <div style={{ textAlign: 'left', fontSize: '11px', maxWidth: '320px' }}>
        <div className="mb-2">
          <strong>📅 Período de Ejecución</strong>
          <div>Desde: {formatDateSafe(dateFrom)} {formatTimeSafe(dateFrom)}</div>
          <div>Hasta: {formatDateSafe(dateTo)} {formatTimeSafe(dateTo)}</div>
        </div>
        
        <div className="mb-2">
          <strong>📊 Análisis Temporal</strong>
          <div>• Total días calendario: {totalDays}</div>
          <div>• Días efectivos: {effectiveDays} ({effectiveDaysPerWeek}/semana)</div>
          {nonWorkingDays > 0 && <div>• Días no laborables: {nonWorkingDays}</div>}
          <div>• Semanas completas: {Math.floor(totalDays / 7)}</div>
        </div>
        
        <div className="mb-2">
          <strong>💰 Distribución Diaria</strong>
          <div style={{ backgroundColor: '#28a745', color: 'white', padding: '4px 8px', borderRadius: '4px', margin: '3px 0', fontWeight: 'bold' }}>
            <strong>Primer día:</strong> {formatCurrency(distInfo?.firstDayAmount || 0)}
          </div>
          <div style={{ backgroundColor: '#007bff', color: 'white', padding: '4px 8px', borderRadius: '4px', margin: '3px 0', fontWeight: 'bold' }}>
            <strong>Días durante:</strong> {formatCurrency(distInfo?.dailyAmountAdjusted || 0)}
          </div>
          <div style={{ backgroundColor: '#fd7e14', color: 'white', padding: '4px 8px', borderRadius: '4px', margin: '3px 0', fontWeight: 'bold' }}>
            <strong>Último día:</strong> {formatCurrency(distInfo?.lastDayAmount || 0)}
          </div>
        </div>
        
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '5px', fontSize: '10px', color: '#666' }}>
          <div><strong>🎯 Estrategia:</strong> {assignment.investmentType}</div>
          <div><strong>💡 Promedio/día:</strong> {formatCurrency((distInfo?.dailyAmountBase || 0))}</div>
          {assignment.notes && <div><strong>📝 Nota:</strong> {assignment.notes.substring(0, 50)}{assignment.notes.length > 50 ? '...' : ''}</div>}
        </div>
      </div>
    </Tooltip>
  ), [assignment.id, assignment.investmentType, assignment.notes, distInfo, dateFrom, dateTo, formatCurrency, totalDays, effectiveDays, effectiveDaysPerWeek, nonWorkingDays]);
  
  return (
    <div className="text-center">
      <OverlayTrigger
        placement="left"
        delay={{ show: 50, hide: 25 }}
        overlay={tooltipContent}
        trigger={['hover', 'focus']}
        rootClose={true}
      >
        <div style={{ fontSize: '11px', lineHeight: '1.2', cursor: 'pointer' }}>
          <div className="text-success mb-1">
            <strong>Primer:</strong> {formatCurrency(distInfo?.firstDayAmount || 0)}
          </div>
          <div className="text-primary mb-1">
            <strong>Durante:</strong> {formatCurrency(distInfo?.dailyAmountAdjusted || 0)}
          </div>
          <div className="text-warning mb-1">
            <strong>Último:</strong> {formatCurrency(distInfo?.lastDayAmount || 0)}
          </div>
          <div className="text-muted" style={{ fontSize: '10px' }}>
            {totalDays} días • {effectiveDaysPerWeek}/sem
          </div>
          <div className="text-info" style={{ fontSize: '9px', marginTop: '2px' }}>
            📊 Click para detalles
          </div>
        </div>
      </OverlayTrigger>
    </div>
  );
};

// Funciones para mostrar fechas EXACTAMENTE como vienen de la base de datos
const formatDateSafe = (dateInput) => {
  if (!dateInput) return 'N/A';
  try {
    if (dateInput instanceof Date) {
      if (isNaN(dateInput.getTime())) return 'Fecha inválida';
      const y = dateInput.getFullYear();
      const m = String(dateInput.getMonth() + 1).padStart(2, '0');
      const d = String(dateInput.getDate()).padStart(2, '0');
      return `${d}/${m}/${y}`;
    }
    const dateString = String(dateInput);
    const parts = dateString.split('T')[0].split('-');
    const year = parts[0];
    const month = parts[1];
    const day = parts[2];
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha inválida';
  }
};

const formatDateTimeSafe = (dateInput) => {
  if (!dateInput) return 'N/A';
  try {
    if (dateInput instanceof Date) {
      if (isNaN(dateInput.getTime())) return 'Fecha inválida';
      const y = dateInput.getFullYear();
      const m = String(dateInput.getMonth() + 1).padStart(2, '0');
      const d = String(dateInput.getDate()).padStart(2, '0');
      const hh = String(dateInput.getHours()).padStart(2, '0');
      const mm = String(dateInput.getMinutes()).padStart(2, '0');
      return `${d}/${m}/${y}, ${hh}:${mm}`;
    }
    const dateString = String(dateInput);
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-');
    const [hour, minute] = timePart.split(':');
    return `${day}/${month}/${year}, ${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting date with time:', error);
    return 'Fecha inválida';
  }
};

const formatTimeSafe = (dateInput) => {
  if (!dateInput) return 'N/A';
  try {
    if (dateInput instanceof Date) {
      if (isNaN(dateInput.getTime())) return '00:00';
      const hh = String(dateInput.getHours()).padStart(2, '0');
      const mm = String(dateInput.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    const dateString = String(dateInput);
    const timePart = dateString.split('T')[1];
    const [hour, minute] = timePart.split(':');
    return `${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return '00:00';
  }
};

const OrderInsertPage = () => {
  const location = useLocation();
  const { filterByAssignedClients } = useAuth();
  
  // Detectar si estamos en la sección de Branding basado en la ruta
  const isBrandingSection = location.pathname.includes('/branding');
  const campaignTypeFilter = isBrandingSection ? 'Branding' : 'Performance';
  
  // Estilos para timeline (inline styles para evitar dependencias externas)
  const timelineStyles = `
    .timeline-simple {
      position: relative;
      padding-left: 20px;
    }
    .timeline-simple::before {
      content: '';
      position: absolute;
      left: 6px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: #e9ecef;
    }
    .timeline-item {
      position: relative;
      margin-bottom: 20px;
    }
    .timeline-marker {
      position: absolute;
      left: -18px;
      top: 3px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid #fff;
      box-shadow: 0 0 0 2px #e9ecef;
    }
    .timeline-content {
      padding-left: 8px;
    }
  `;

  const [, setActiveTab] = useState('basic');
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [assignmentActiveTab, setAssignmentActiveTab] = useState('summary');
  // eslint-disable-next-line no-unused-vars
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  // Estados para modal de eliminación de asignaciones
  const [showDeleteAssignmentModal, setShowDeleteAssignmentModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  
  // Estados para modales
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [showAdminFeeModal, setShowAdminFeeModal] = useState(false);
  
  // Estados para modal de historial
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyEntityType, setHistoryEntityType] = useState(null);
  const [historyEntityId, setHistoryEntityId] = useState(null);
  const [historyEntityName, setHistoryEntityName] = useState('');
  
  // Estados para asignaciones de presupuesto
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  
  // Estados adicionales para la interfaz
  
  // Estados para cálculo de presupuesto
  const [selectedCalculationAssignment, setSelectedCalculationAssignment] = useState(null);
  const [calculationDistributionInfo, setCalculationDistributionInfo] = useState(null);
  


  // Helper para crear columnas
  const columnHelper = createColumnHelper();
  
  const [formData, setFormData] = useState({
    clienteId: '',
    clienteName: '',
    businessUnit: '',
    descripcion: '',
    fechaDesde: null,
    fechaHasta: null,
    moneda: 'USD',
    inversionBruta: '',
    inversionNeta: '',
    brokerFee: '',
    brokerFeeType: 'percentage', // 'percentage' o 'amount'
    mediaFee: '',
    mediaFeeType: 'percentage', // 'percentage' o 'amount'
    feeAdmin: '',
    feeAdminType: 'percentage', // 'percentage' o 'amount'
    nivelAsignacion: 'ACCOUNT',
    cuentaId: '',
    accountId: '',
    notes: ''
  });

  // Estados para nueva asignación
  const [assignmentFormData, setAssignmentFormData] = useState({
    description: '',
    investmentType: 'CPM',
    dateFrom: null,
    dateTo: null,
    amount: '',
    amountType: 'fixed', // 'fixed' para USD, 'percentage' para porcentaje
    daysPerWeek: 7,
    dailyAmount: '',
    weeklyAmount: '',
    campaignId: '',
    notes: ''
  });

  const [showAlert, setShowAlert] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [validationAlert, setValidationAlert] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [validationMessage, setValidationMessage] = useState(null);
  
  // Estados para datos del backend
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false); // Flag para evitar recargar clientes
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsCache, setAccountsCache] = useState({}); // Cache para evitar llamadas duplicadas
  const [loadingAccountsFor, setLoadingAccountsFor] = useState(null); // Track which client is being loaded
  
  // Estados de loading adicionales
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [deletingAssignment, setDeletingAssignment] = useState(false);
  
  // Estados para el modal de Fee Admin
  // eslint-disable-next-line no-unused-vars
  const [selectedAdminFee, setSelectedAdminFee] = useState(null);

  const businessUnits = ['SOCIAL', 'SEARCH', 'DISPLAY', 'VIDEO', 'TV', 'RADIO', 'PRINT'];
  const investmentTypes = ['CPM', 'CPC', 'CPL/CPA'];

  // Cargar clientes al montar el componente
  useEffect(() => {
    loadClients();
  }, []);

  // OPTIMIZACIÓN: Cargar cuentas cuando cambia el cliente (con debounce más largo para evitar llamadas múltiples)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.clienteId) {
        // Solo cargar si no están ya cargadas o en proceso
        if (!accountsCache[formData.clienteId] && loadingAccountsFor !== formData.clienteId) {
          loadAccountsByClient(formData.clienteId);
        } else if (accountsCache[formData.clienteId]) {
          // Si ya están en cache, usarlas directamente
          setAvailableAccounts(accountsCache[formData.clienteId]);
        }
      } else {
        setAvailableAccounts([]);
      }
    }, 300); // Debounce de 300ms (más largo para evitar llamadas múltiples)

    return () => clearTimeout(timeoutId);
  }, [formData.clienteId]); // ARREGLO: Remover accountsCache y loadingAccountsFor para evitar ciclo infinito

  // Cargar órdenes existentes al montar el componente y cuando cambie la ruta
  useEffect(() => {
    loadInsertionOrders();
  }, [campaignTypeFilter]); // Recargar cuando cambie el tipo de campaña

  // Cargar asignaciones cuando se selecciona una orden
  useEffect(() => {
    if (selectedOrder) {
      loadAssignments(selectedOrder.id);
    } else {
      setAssignments([]);
    }
  }, [selectedOrder]);

  // Efecto para hacer las filas clickeables (fallback si onRowClick no funciona)
  useEffect(() => {
    const handleRowClick = (e) => {
      // Evitar si se hace clic en botones o elementos interactivos
      if (e.target.closest('button') || e.target.closest('.btn') || e.target.closest('[role="button"]')) {
        return;
      }
      
      const row = e.target.closest('tr[data-row-index]');
      if (row) {
        const rowIndex = parseInt(row.getAttribute('data-row-index'));
        const order = orders[rowIndex];
        if (order) {
          handleOrderSelect(order);
        }
      }
    };

    // Agregar listener a todo el documento con captura
    document.addEventListener('click', handleRowClick, true);
    return () => document.removeEventListener('click', handleRowClick, true);
  }, []); // ARREGLO: Remover orders de dependencias para evitar re-renders innecesarios

  // Efecto adicional para filas de asignaciones clickeables
  useEffect(() => {
    const handleAssignmentRowClick = (e) => {
      // Evitar si se hace clic en botones o elementos interactivos
      if (e.target.closest('button') || e.target.closest('.btn') || e.target.closest('[role="button"]')) {
        return;
      }
      
      // Solo procesar si estamos en la tabla de asignaciones
      const assignmentTable = e.target.closest('.table-responsive');
      if (!assignmentTable || !assignmentTable.closest('[data-assignment-section]')) {
        return;
      }
      
      const row = e.target.closest('tr[data-row-index]');
      if (row) {
        const rowIndex = parseInt(row.getAttribute('data-row-index'));
        const assignment = assignments[rowIndex];
        if (assignment) {
          handleAssignmentSelect(assignment);
        }
      }
    };

    // Agregar listener específico para asignaciones
    document.addEventListener('click', handleAssignmentRowClick, true);
    return () => document.removeEventListener('click', handleAssignmentRowClick, true);
  }, [assignments]);

  const loadClients = async () => {
    // Si ya están cargados, no recargar
    if (clientsLoaded) {
      return;
    }

    setLoadingClients(true);
    try {
      const result = await clientService.getAllClients();
      
      if (result.success) {
        // Filtrar clientes por los asignados al usuario
        const filteredClients = filterByAssignedClients(result.clients, 'ClientID');
        setClients(filteredClients);
        setClientsLoaded(true);
        
        // Si hay un clienteId en formData pero no clienteName, intentar asignarlo
        if (formData.clienteId && !formData.clienteName) {
          const clientIdNumber = parseInt(formData.clienteId);
          const selectedClient = filteredClients.find(c => c.ClientID === clientIdNumber);
          if (selectedClient) {
            setFormData(prev => ({
              ...prev,
              clienteName: selectedClient.ClientName
            }));
          }
        }
      } else {
        console.error('Error loading clients:', result.error);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadAccountsByClient = useCallback(async (clientId) => {
    if (!clientId) {
      setAvailableAccounts([]);
      return;
    }

    // OPTIMIZACIÓN: Evitar llamadas duplicadas de manera más estricta
    if (loadingAccountsFor === clientId || loadingAccounts) {
      return;
    }

    // Si ya tenemos las cuentas en cache, usarlas
    if (accountsCache[clientId]) {
      setAvailableAccounts(accountsCache[clientId]);
      return;
    }

    setLoadingAccounts(true);
    setLoadingAccountsFor(clientId);
    
    try {
      const result = await budgetService.getAccountsByClient(clientId);
      
      if (result.success) {
        const accounts = result.accounts || [];
        setAvailableAccounts(accounts);
        
        // Guardar en cache
        setAccountsCache(prev => ({
          ...prev,
          [clientId]: accounts
        }));

        // Limpiar selección cuando se cargan nuevas cuentas
        setFormData(prev => ({
          ...prev,
          cuentaId: '',
          accountId: ''
        }));
      } else {
        console.error('Error loading accounts:', result.error);
        setAvailableAccounts([]);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      setAvailableAccounts([]);
    } finally {
      setLoadingAccounts(false);
      setLoadingAccountsFor(null);
    }
  }, [accountsCache, loadingAccountsFor, loadingAccounts]);

  // Cargar órdenes existentes
  const loadInsertionOrders = async () => {
    setLoading(true);
    try {
      // Usar el filtro de campaignType basado en la ruta actual
      const result = await budgetService.getAllInsertionOrders(campaignTypeFilter);
      if (result.success) {
        // Formatear las órdenes del backend al formato del frontend
        const rawOrders = result.data || result.orders || [];
        const formattedOrders = rawOrders.map(order => {
          return {
          id: order.id,
          cliente: order.clientName,
            clienteId: order.client_id || null, // Cambiado a número
            clienteName: order.clientName,
          businessUnit: order.businessUnit,
          descripcion: order.description,
          fechaDesde: order.dateFrom,
          fechaHasta: order.dateTo,
          moneda: order.currency,
          inversionBruta: parseFloat(order.grossInvestment) || 0,
          inversionNeta: parseFloat(order.netInvestment) || 0,
          brokerFee: order.brokerFee || 0,
          mediaFee: order.mediaFee || 0,
          nivelAsignacion: order.nivel || 'ACCOUNT',
          cuentaId: order.accountId,
          accountId: order.accountId,
          accountName: order.accountName,
          accountType: order.accountType,
          accountIdentifier: order.accountIdentifier,
          notes: order.description,
          fechaCreacion: order.createdAt,
          fechaModificacion: order.updatedAt,
          status: order.isActive ? 'Activa' : 'Inactiva',
          nivel: order.nivel,
          campaignId: order.campaignId || '',
          selectedAccountForCampaigns: order.nivel === 'CAMPAIGN' ? order.accountId : '',
          ...order
        };
        });
        setOrders(formattedOrders);
      } else {
        console.error('Error loading orders:', result.error);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para recalcular montos usando el algoritmo avanzado de BudgetControl
  const recalculateAssignmentAmounts = (assignment, selectedOrder) => {
    if (!assignment || !selectedOrder) return assignment;

    let realAmount = parseFloat(assignment.montoValor) || 0;
    
    // Si es porcentaje, convertir a dólares
    const isPercentage = assignment.amountType === 'percentage' || 
                        (!assignment.amountType && realAmount <= 100 && realAmount > 0);
    
    if (isPercentage && selectedOrder.inversionNeta) {
      realAmount = (realAmount * selectedOrder.inversionNeta) / 100;
    }

    // USAR EL ALGORITMO AVANZADO DE BUDGETCONTROL
    try {
      const advancedCalculation = calculateAdvancedBudgetDistribution({
        dateFrom: new Date(assignment.dateFrom),
        dateTo: new Date(assignment.dateTo),
        totalAmount: realAmount,
        daysPerWeek: parseInt(assignment.diasSemanaCorre) || 7
      });

      return {
        ...assignment,
        dailyAmount: advancedCalculation.dailyAmount,
        weeklyAmount: advancedCalculation.weeklyAmount,
        distributionInfo: {
          ...assignment.distributionInfo,
          totalDays: advancedCalculation.totalDays,
          totalWeeks: advancedCalculation.totalWeeks,
          effectiveDaysPerWeek: parseInt(assignment.diasSemanaCorre) || 7,
          dailyAmountBase: advancedCalculation.dailyAmountBase,
          dailyAmountAdjusted: advancedCalculation.dailyAmountAdjusted,
          firstDayAmount: advancedCalculation.firstDayAmount,
          lastDayAmount: advancedCalculation.lastDayAmount,
          weeklyAmountEstimate: advancedCalculation.weeklyAmount,
          // INFORMACIÓN AVANZADA DE BUDGETCONTROL
          dailyBreakdown: advancedCalculation.dailyBreakdown,
          weeklyBreakdown: advancedCalculation.weeklyBreakdown,
          summaryDisplay: advancedCalculation.summary?.display,
          weeklySummaryDisplay: advancedCalculation.summary?.weeklyDisplay
        }
      };
    } catch (error) {
      console.error('Error usando calculador avanzado, usando fallback:', error);
    }

    // FALLBACK: Algoritmo simple si falla el avanzado
    const dateFrom = new Date(assignment.dateFrom);
    const dateTo = new Date(assignment.dateTo);
    const totalDays = Math.ceil((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1;
    const daysPerWeek = parseInt(assignment.diasSemanaCorre) || 7;
    const totalWeeks = Math.ceil(totalDays / 7);
    const effectiveDays = Math.ceil((totalDays * daysPerWeek) / 7);
    const dailyAmount = effectiveDays > 0 ? realAmount / effectiveDays : 0;
    const weeklyAmount = totalWeeks > 0 ? realAmount / totalWeeks : 0;

    return {
      ...assignment,
      dailyAmount: parseFloat(dailyAmount.toFixed(2)),
      weeklyAmount: parseFloat(weeklyAmount.toFixed(2)),
      distributionInfo: {
        ...assignment.distributionInfo,
        totalDays,
        totalWeeks,
        effectiveDaysPerWeek: daysPerWeek,
        dailyAmountBase: parseFloat(dailyAmount.toFixed(2)),
        dailyAmountAdjusted: parseFloat(dailyAmount.toFixed(2)),
        firstDayAmount: parseFloat(dailyAmount.toFixed(2)),
        lastDayAmount: parseFloat(dailyAmount.toFixed(2)),
        weeklyAmountEstimate: parseFloat(weeklyAmount.toFixed(2))
      }
    };
  };

  const loadAssignments = async (orderId) => {
    setLoadingAssignments(true);
    try {
      const result = await budgetService.getAssignmentsByOrderId(orderId);
      if (result.success) {
        // Formatear asignaciones del backend al formato del frontend
        const formattedAssignments = (result.assignments || []).map(assignment => {
          const parseIsoToLocalDate = (iso) => {
            if (!iso) return null;
            try {
              const [datePart, timePart = '00:00'] = String(iso).split('T');
              const [y, m, d] = datePart.split('-').map(n => parseInt(n, 10));
              const [hh = '00', mm = '00'] = timePart.split(':');
              const local = new Date(y, (m || 1) - 1, d || 1);
              local.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
              return local;
            } catch {
              const d = new Date(iso);
              return isNaN(d.getTime()) ? null : d;
            }
          };

          const baseAssignment = {
            id: assignment.id,
            orderId: assignment.insertionOrderId,
            description: assignment.description,
            investmentType: assignment.investmentType || 'CPM',
            dateFrom: parseIsoToLocalDate(assignment.dateFrom),
            dateTo: parseIsoToLocalDate(assignment.dateTo),
            amount: assignment.montoValor,
            amountType: assignment.amountType || 'fixed', // IMPORTANTE: Mapear amountType
            montoValor: assignment.montoValor, // Mantener valor original
            daysPerWeek: parseInt(assignment.diasSemanaCorre) || 7,
            dailyAmount: assignment.dailyAmount || 0,
            weeklyAmount: assignment.weeklyAmount || 0,
            campaignId: assignment.campaignId,
            notes: assignment.notes || '',
            status: assignment.isActive ? 'active' : 'inactive',
            createdAt: assignment.createdAt,
            updatedAt: assignment.updatedAt,
            distributionInfo: {
              totalDays: assignment.diasSemanaCorre ? parseInt(assignment.diasSemanaCorre) : 0,
              totalWeeks: assignment.diasSemanaCorre ? Math.ceil(parseInt(assignment.diasSemanaCorre) / 7) : 0,
              effectiveDaysPerWeek: parseInt(assignment.diasSemanaCorre) || 7,
              dailyAmountBase: assignment.dailyAmount || 0,
              dailyAmountAdjusted: assignment.dailyAmount || 0,
              firstDayAmount: assignment.dailyAmount || 0,
              lastDayAmount: assignment.dailyAmount || 0,
              weeklyAmountEstimate: assignment.weeklyAmount || 0
            }
          };
          
          // Recalcular montos correctamente
          return recalculateAssignmentAmounts(baseAssignment, selectedOrder);
        });
        
        setAssignments(formattedAssignments);
      } else {
        console.error('Error loading assignments:', result.error);
        setAssignments([]);
      }
    } catch (error) {
      console.error('Error loading assignments:', error);
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  // Función auxiliar para obtener el nombre del cliente
  const getClientNameById = (clientId) => {
    if (!clientId || !clients || clients.length === 0) return '';
    
    const clientIdNumber = parseInt(clientId);
    const selectedClient = clients.find(c => c.ClientID === clientIdNumber);
    return selectedClient ? selectedClient.ClientName : '';
  };

  const handleInputChange = (field, value) => {
    // Limpiar alerts cuando el usuario empiece a escribir
    if (showValidationAlert) {
      setShowValidationAlert(false);
      setValidationMessage(null);
    }
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Manejar cambio de cliente
      if (field === 'clienteId') {
        // Usar la función auxiliar para obtener el nombre
        const clientName = getClientNameById(value);
        newData.clienteName = clientName;
        // Resetear selección de cuenta cuando cambia el cliente
        newData.cuentaId = '';
        newData.accountId = '';
        
        // Log solo si hay problemas
        if (!clientName && value) {
          console.error('❌ No se encontró cliente con ID:', value);
        }
      }

      // Cálculos automáticos para fees
      const inversionBruta = parseFloat(newData.inversionBruta) || 0;
      let calculatedInversionNeta = inversionBruta;

      // Calcular Broker Fee
      if (newData.brokerFee && inversionBruta > 0) {
        const brokerFeeValue = parseFloat(newData.brokerFee) || 0;
        if (newData.brokerFeeType === 'percentage') {
          calculatedInversionNeta -= (inversionBruta * brokerFeeValue / 100);
        } else {
          calculatedInversionNeta -= brokerFeeValue;
        }
      }

      // Calcular Media Fee
      if (newData.mediaFee && inversionBruta > 0) {
        const mediaFeeValue = parseFloat(newData.mediaFee) || 0;
        if (newData.mediaFeeType === 'percentage') {
          calculatedInversionNeta -= (inversionBruta * mediaFeeValue / 100);
        } else {
          calculatedInversionNeta -= mediaFeeValue;
        }
      }

      // Calcular Fee Admin
      if (newData.feeAdmin && inversionBruta > 0) {
        const feeAdminValue = parseFloat(newData.feeAdmin) || 0;
        if (newData.feeAdminType === 'percentage') {
          calculatedInversionNeta -= (inversionBruta * feeAdminValue / 100);
        } else {
          calculatedInversionNeta -= feeAdminValue;
        }
      }

      // Solo actualizar inversión neta si cambiaron los valores relacionados
      if (['inversionBruta', 'brokerFee', 'brokerFeeType', 'mediaFee', 'mediaFeeType', 'feeAdmin', 'feeAdminType'].includes(field)) {
        newData.inversionNeta = calculatedInversionNeta.toFixed(2);
      }

      // Validaciones en tiempo real
      const errors = { ...validationErrors };

      // Validación para fechas
      if (field === 'fechaHasta' && newData.fechaDesde && value) {
        if (value <= newData.fechaDesde) {
          errors.fechaHasta = 'La fecha y hora de fin debe ser posterior a la de inicio';
          toast.warn('La fecha y hora de fin debe ser posterior a la de inicio');
        } else {
          delete errors.fechaHasta;
        }
      }

      // Validación para cuenta
      if (field === 'cuentaId') {
        if (!value) {
          errors.cuentaId = 'Debe seleccionar una cuenta publicitaria';
        } else {
          delete errors.cuentaId;
        }
        // Sincronizar accountId con el valor seleccionado del combobox
        newData.accountId = value || '';
      }

      // Validación para cliente
      if (field === 'clienteId') {
        if (!value) {
          errors.clienteId = 'Debe seleccionar un cliente';
        } else {
          delete errors.clienteId;
        }
      }

      setValidationErrors(errors);
      return newData;
    });
  };


  // Función para validar todos los campos obligatorios

  // Función para seleccionar una orden y cargar sus asignaciones
  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    setAssignmentActiveTab('summary');
  };

  // Función para manejar selección de asignación
  const handleAssignmentSelect = (assignment) => {
    
    // Parser flexible: acepta Date (lo deja igual) o string ISO (lo arma sin desfase)
    const parseDateFlexible = (value) => {
      if (!value) return null;
      // Ya es Date válido
      if (value instanceof Date && !isNaN(value.getTime())) return value;
      try {
        const str = String(value);
        // Si incluye 'T' asumo ISO: YYYY-MM-DDTHH:mm:ssZ
        if (str.includes('T')) {
          const [datePart, timePart = '00:00'] = str.split('T');
          const [y, m, d] = datePart.split('-').map(n => parseInt(n, 10));
          if (!y || !m || !d) return null;
          const [hh = '00', mm = '00'] = timePart.split(':');
          const hours = parseInt(hh, 10);
          const minutes = parseInt(mm, 10);
          const local = new Date(y, m - 1, d);
          local.setHours(hours, minutes, 0, 0);
          return isNaN(local.getTime()) ? null : local;
        }
        // Intentar parsear por new Date como fallback (e.g. "Mon Sep 01 2025 ...")
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : d;
      } catch {
        return null;
      }
    };

    const dateFromObj = parseDateFlexible(assignment.dateFrom);
    const dateToObj = parseDateFlexible(assignment.dateTo);
    
    setEditingAssignment(assignment);
    setAssignmentFormData({
      description: assignment.description || '',
      investmentType: assignment.investmentType || 'CPM',
      dateFrom: dateFromObj,
      dateTo: dateToObj,
      amount: assignment.montoValor || assignment.amount || '',
      amountType: assignment.amountType || 'fixed',
      daysPerWeek: assignment.daysPerWeek || 7,
      dailyAmount: assignment.dailyAmount || '',
      weeklyAmount: assignment.weeklyAmount || '',
      campaignId: assignment.campaignId || '',
      notes: assignment.notes || ''
    });
    setAssignmentActiveTab('new-assignment');
  };

  // Función para manejar inputs de asignación
  const handleAssignmentInputChange = (field, value) => {
    setAssignmentFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Usar el algoritmo avanzado de BudgetControl en tiempo real
      if (field === 'amount' || field === 'amountType' || field === 'daysPerWeek' || field === 'dateFrom' || field === 'dateTo') {
        let amount = parseFloat(newData.amount) || 0;
        
        // Si es porcentaje, convertir a valor en dólares
        if (newData.amountType === 'percentage' && selectedOrder?.inversionNeta) {
          amount = (amount * parseFloat(selectedOrder.inversionNeta)) / 100;
        }
        
        const daysPerWeek = parseInt(newData.daysPerWeek) || 7;
        const dateFrom = newData.dateFrom ? new Date(newData.dateFrom) : null;
        const dateTo = newData.dateTo ? new Date(newData.dateTo) : null;
        
        if (dateFrom && dateTo && amount > 0) {
          // USAR ALGORITMO AVANZADO DE BUDGETCONTROL EN TIEMPO REAL
          try {
            const advancedCalculation = calculateAdvancedBudgetDistribution({
              dateFrom,
              dateTo,
              totalAmount: amount,
              daysPerWeek
            });

            // Actualizar inmediatamente con los cálculos avanzados
            newData.dailyAmount = advancedCalculation.dailyAmount.toFixed(2);
            newData.weeklyAmount = advancedCalculation.weeklyAmount.toFixed(2);
            newData.distributionInfo = {
              totalDays: advancedCalculation.totalDays,
              totalWeeks: advancedCalculation.totalWeeks,
              effectiveDaysPerWeek: advancedCalculation.effectiveDaysPerWeek,
              dailyAmountBase: advancedCalculation.dailyAmountBase,
              dailyAmountAdjusted: advancedCalculation.dailyAmountAdjusted,
              firstDayAmount: advancedCalculation.firstDayAmount,
              lastDayAmount: advancedCalculation.lastDayAmount,
              weeklyAmountEstimate: advancedCalculation.weeklyAmountEstimate,
              // Información avanzada de BudgetControl
              dailyBreakdown: advancedCalculation.dailyBreakdown,
              weeklyBreakdown: advancedCalculation.weeklyBreakdown,
              summaryDisplay: advancedCalculation.summary?.display,
              weeklySummaryDisplay: advancedCalculation.summary?.weeklyDisplay,
              firstLastInfo: advancedCalculation.summary?.firstLastInfo
            };
            
            // Información adicional para la UI
            newData.campaignDuration = advancedCalculation.summary?.display || `${advancedCalculation.totalDays} días`;
            newData.firstDayInfo = `Primer día: $${advancedCalculation.firstDayAmount.toFixed(2)}`;
            newData.lastDayInfo = `Último día: $${advancedCalculation.lastDayAmount.toFixed(2)}`;
            newData.weeklyInfo = `Semanal: $${advancedCalculation.weeklyAmount.toFixed(2)}`;
            newData.dailyInfo = `Diario ajustado: $${advancedCalculation.dailyAmountAdjusted.toFixed(2)}`;
            
          } catch (error) {
            console.error('Error en cálculo avanzado, usando fallback:', error);
            // Fallback a cálculo simple si falla el avanzado
            const totalDays = Math.ceil((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1;
            const effectiveDays = Math.ceil((totalDays * daysPerWeek) / 7);
            const totalWeeks = Math.ceil(totalDays / 7);
            const dailyAmount = effectiveDays > 0 ? amount / effectiveDays : 0;
            const weeklyAmount = totalWeeks > 0 ? amount / totalWeeks : 0;
            
            newData.dailyAmount = dailyAmount.toFixed(2);
            newData.weeklyAmount = weeklyAmount.toFixed(2);
            newData.distributionInfo = {
              totalDays,
              totalWeeks,
              effectiveDaysPerWeek: daysPerWeek,
              dailyAmountBase: dailyAmount,
              dailyAmountAdjusted: dailyAmount,
              firstDayAmount: dailyAmount,
              lastDayAmount: dailyAmount,
              weeklyAmountEstimate: weeklyAmount
            };
          }
        } else {
          // Limpiar cálculos si faltan datos
          newData.dailyAmount = '';
          newData.weeklyAmount = '';
          newData.distributionInfo = null;
          newData.campaignDuration = '';
          newData.firstDayInfo = '';
          newData.lastDayInfo = '';
          newData.weeklyInfo = '';
          newData.dailyInfo = '';
        }
      }

      return newData;
    });
  };

  // Función para validar exceso de presupuesto
  const validateBudgetExcess = (newAssignmentAmount) => {
    if (!selectedOrder) return { isValid: true };

    const totalCurrentAssignments = assignments.reduce((sum, assignment) => 
      sum + (parseFloat(assignment.amount) || 0), 0
    );
    
    // Convertir a dólares si es porcentaje
    let amountInDollars = parseFloat(newAssignmentAmount) || 0;
    if (assignmentFormData.amountType === 'percentage' && selectedOrder?.inversionNeta) {
      amountInDollars = (amountInDollars * parseFloat(selectedOrder.inversionNeta)) / 100;
    }
    
    const totalWithNewAssignment = totalCurrentAssignments + amountInDollars;
    const netInvestment = parseFloat(selectedOrder.inversionNeta) || 0;
    
    const exceedsBy = totalWithNewAssignment - netInvestment;
    const percentageUsed = (totalWithNewAssignment / netInvestment) * 100;

    return {
      isValid: totalWithNewAssignment <= netInvestment,
      totalCurrentAssignments,
      totalWithNewAssignment,
      netInvestment,
      exceedsBy: exceedsBy > 0 ? exceedsBy : 0,
      percentageUsed,
      remainingBudget: netInvestment - totalCurrentAssignments
    };
  };

  // Función para crear/actualizar asignación (SIN validaciones - ya están en el botón)
  const handleCreateAssignment = async () => {
    setSavingAssignment(true);
    try {
      // Preparar datos para el backend usando la función correcta
      let assignmentData;
      if (editingAssignment) {
        // Para actualizar: NO incluir insertionOrderId
        assignmentData = budgetService.formatAssignmentDataForUpdate({
          ...assignmentFormData,
          orderId: selectedOrder.id
        });
      } else {
        // Para crear: SÍ incluir insertionOrderId
        assignmentData = budgetService.formatAssignmentDataForApi({
          ...assignmentFormData,
          orderId: selectedOrder.id
        });
      }

      let result;
      if (editingAssignment) {
        // Actualizar asignación existente
        result = await budgetService.updateAssignment(editingAssignment.id, assignmentData);
      } else {
        // Crear nueva asignación
        result = await budgetService.createAssignment(assignmentData);
      }
      
      if (result.success) {
        // Recargar las asignaciones desde el backend
        await loadAssignments(selectedOrder.id);
        
        // Resetear el formulario y estado de edición
        setAssignmentFormData({
          description: '',
          investmentType: 'CPM',
          dateFrom: null,
          dateTo: null,
          amount: '',
          amountType: 'fixed',
          daysPerWeek: 7,
          dailyAmount: '',
          weeklyAmount: '',
          campaignId: '',
          notes: ''
        });
        setEditingAssignment(null);

        toast.success(editingAssignment ? 
          'Asignación actualizada exitosamente' : 
          'Asignación creada exitosamente', {
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        
        // Cambiar a summary tanto para editar como para crear nuevas asignaciones
        setAssignmentActiveTab('summary');
      } else {
        toast.error(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error(editingAssignment ? 
        '❌ Error al actualizar la asignación' : 
        '❌ Error al crear la asignación');
    } finally {
      setSavingAssignment(false);
    }
  };

  // Función para mostrar modal de confirmación de eliminar asignación
  const handleDeleteAssignment = (assignment) => {
    setAssignmentToDelete(assignment);
    setShowDeleteAssignmentModal(true);
  };

  // Función para confirmar la eliminación de asignación
  const confirmDeleteAssignment = async () => {
    setDeletingAssignment(assignmentToDelete.id);
    try {
      // Llamar al backend para eliminar la asignación
      const result = await budgetService.deleteAssignment(assignmentToDelete.id);
      
      if (result.success) {
        // Recargar las asignaciones desde el backend si hay una orden seleccionada
        if (selectedOrder) {
          await loadAssignments(selectedOrder.id);
        }
        setShowDeleteAssignmentModal(false);
        setAssignmentToDelete(null);
        toast.success('✅ Asignación eliminada exitosamente');
      } else {
        toast.error(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('❌ Error al eliminar la asignación');
    } finally {
      setDeletingAssignment(false);
    }
  };


  const handleEdit = (order) => {
    
    setEditingOrder(order);
    
    // Mapear los datos del backend al estado del formulario
    // Parsear fechas directamente del ISO string sin conversiones de timezone
    const dateFromStr = order.dateFrom || order.fechaDesde || '';
    const dateToStr = order.dateTo || order.fechaHasta || '';
    
    let fechaDesde = '', horaDesde = '00:00';
    let fechaHasta = '', horaHasta = '23:59';
    
        if (dateFromStr) {
      try {
        const [datePart, timePart] = dateFromStr.split('T');
        if (datePart) {
          const [year, month, day] = datePart.split('-');
          if (year && month && day && !isNaN(year) && !isNaN(month) && !isNaN(day)) {
            // Crear Date object directamente desde los componentes ISO sin conversión de timezone
            const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            if (timePart) {
              const [hours, minutes] = timePart.split(':');
              if (hours && minutes && !isNaN(hours) && !isNaN(minutes)) {
                dateObj.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                horaDesde = `${hours}:${minutes}`;
              }
            }
            // Validar que el Date es válido
            if (!isNaN(dateObj.getTime())) {
              fechaDesde = dateObj;
            }
          }
        }
      } catch (error) {
        console.error('🔍 HANDLEEDIT - Error parseando dateFrom:', error, dateFromStr);
      }
    }
    
    if (dateToStr) {
      try {
        const [datePart, timePart] = dateToStr.split('T');
        if (datePart) {
          const [year, month, day] = datePart.split('-');
          if (year && month && day && !isNaN(year) && !isNaN(month) && !isNaN(day)) {
            // Crear Date object directamente desde los componentes ISO sin conversión de timezone
            const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            if (timePart) {
              const [hours, minutes] = timePart.split(':');
              if (hours && minutes && !isNaN(hours) && !isNaN(minutes)) {
                dateObj.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                horaHasta = `${hours}:${minutes}`;
              }
            }
            // Validar que el Date es válido
            if (!isNaN(dateObj.getTime())) {
              fechaHasta = dateObj;
            }
          }
        }
      } catch (error) {
        console.error('🔍 HANDLEEDIT - Error parseando dateTo:', error, dateToStr);
      }
    }

    const mappedData = {
      id: order.id,
      clienteId: String(order.clientId || order.client_id || ''),
      clienteName: order.clientName || order.cliente || '',
      businessUnit: order.businessUnit || '',
      descripcion: order.description || order.descripcion || '',
      fechaDesde,
      fechaHasta,
      horaDesde,
      horaHasta,
      moneda: order.currency || order.moneda || 'USD',
      inversionBruta: parseFloat(order.grossInvestment ?? order.inversionBruta ?? 0),
      inversionNeta: parseFloat(order.netInvestment ?? order.inversionNeta ?? 0),
      brokerFee: order.brokerFee || '',
      brokerFeeType: 'percentage',
      mediaFee: order.mediaFee || '',
      mediaFeeType: 'percentage',  
      feeAdmin: '',
      feeAdminType: 'percentage',
      totalFees: '',
      nivelAsignacion: order.nivel === 'ACCOUNT' ? 'Cuenta' : (order.nivel === 'CAMPAIGN' ? 'Campañas' : 'Cuenta'),
      cuentaId: order.cuentaId,
      accountId: order.accountId || order.cuentaId || '',
      campaignId: order.campaignId || '',
      selectedAccountForCampaigns: order.accountId || order.cuentaId || '',
      notes: order.notes || ''
    };
    
    
    // Cargar las cuentas del cliente en modo edición
    if (order.client_id) {
      loadAccountsByClient(order.client_id);
    }
    
    setFormData(mappedData);
    setEditingOrder(mappedData); // Usar mappedData en lugar del objeto original
    setShowNewOrderModal(true);
  };

  const handleDelete = (order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    
    try {
      const result = await budgetService.deleteInsertionOrder(orderToDelete.id);
      if (result.success) {
        await loadInsertionOrders(); // Recargar las órdenes
        setShowDeleteModal(false);
        setOrderToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting order:', error);
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      clienteId: '',
      clienteName: '',
      businessUnit: '',
      descripcion: '',
      fechaDesde: null,
      fechaHasta: null,
      moneda: 'USD',
      inversionBruta: '',
      inversionNeta: '',
      brokerFee: '',
      brokerFeeType: 'percentage',
      mediaFee: '',
      mediaFeeType: 'percentage',
      feeAdmin: '',
      feeAdminType: 'percentage',
      nivelAsignacion: 'ACCOUNT',
      cuentaId: '',
      notes: ''
    });
    setValidationErrors({});
    setActiveTab('basic');
    setEditingOrder(null);
  };

  // Función para mostrar la información de la cuenta con enlace
  const renderAccountInfo = (order) => {
    if (!order.cuentaId && !order.accountId) {
      return (
        <div>
          <div className="text-muted">Sin cuenta</div>
        </div>
      );
    }

    const accountId = order.accountId || order.cuentaId;
    const accountName = order.accountName;
    const accountType = order.accountType;
    const accountIdentifier = order.accountIdentifier;

    if (accountName) {
      return (
        <div>
          <div className="fw-semibold text-primary">
            <a 
              href={`/performance/account/${accountId}`}
              className="text-decoration-none text-primary"
              title={`Ver gestión de cuenta: ${accountName}`}
            >
              <FontAwesomeIcon icon="external-link-alt" className="me-1 fs-11" />
              {accountName}
            </a>
          </div>
          <div className="text-muted fs-11">({accountType})</div>
          <div className="text-muted fs-11">ID: {accountIdentifier}</div>
        </div>
      );
    }

    // Si no tiene accountName, mostrar solo el ID con enlace básico
    return (
      <div>
        <div className="text-warning">
          <a 
            href={`/performance/account/${accountId}`}
            className="text-decoration-none text-warning"
            title={`Ver gestión de cuenta ID: ${accountId}`}
          >
            <FontAwesomeIcon icon="external-link-alt" className="me-1 fs-11" />
            ID: {accountId}
          </a>
        </div>
        <div className="text-muted fs-11">Información no disponible</div>
      </div>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };


  const formatDateOnly = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
      let dateString;
      if (dateInput instanceof Date) {
        if (isNaN(dateInput.getTime())) return 'Fecha inválida';
        dateString = dateInput.toISOString();
      } else {
        dateString = String(dateInput);
      }
      const parts = dateString.split('T')[0].split('-');
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Fecha inválida';
    }
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Extraer directamente del string ISO sin conversiones
      const [datePart, timePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      return `${day}/${month}/${year}, ${hour}:${minute}`;
    } catch (error) {
      console.error('Error formatting date with time:', error);
      return 'Fecha inválida';
    }
  };

  // Definir columnas para la tabla paginada
  const columns = [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: info => info.getValue(),
      meta: {
        headerProps: { style: { width: '80px' } },
        cellProps: { className: 'fw-bold' }
      }
    }),
    columnHelper.accessor('cliente', {
      header: 'Cliente',
      cell: info => info.getValue(),
      meta: {
        headerProps: { style: { minWidth: '150px' } }
      }
    }),
    columnHelper.accessor('businessUnit', {
      header: 'Business Unit',
      cell: info => (
        <Badge bg="primary" className="fs-10">
          {info.getValue()}
        </Badge>
      ),
      meta: {
        headerProps: { style: { width: '120px' } }
      }
    }),
    columnHelper.accessor('descripcion', {
      header: 'Descripción',
      cell: info => (
        <div className="text-truncate" style={{ maxWidth: '200px' }} title={info.getValue()}>
          {info.getValue()}
        </div>
      ),
      meta: {
        headerProps: { style: { minWidth: '200px' } }
      }
    }),
    columnHelper.accessor('fechaDesde', {
      header: 'Fecha Inicio',
      cell: info => (
        <div>
          <div className="fw-semibold">{formatDateWithTime(info.getValue())}</div>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '150px' } }
      }
    }),
    columnHelper.accessor('fechaHasta', {
      header: 'Fecha Fin',
      cell: info => (
        <div>
          <div className="fw-semibold">{formatDateWithTime(info.getValue())}</div>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '150px' } }
      }
    }),
    columnHelper.accessor('inversionBruta', {
      header: 'Inversión Bruta',
      cell: info => {
        const row = info.row.original;
        return formatCurrency(info.getValue(), row.moneda);
      },
      meta: {
        headerProps: { style: { width: '140px' } },
        cellProps: { className: 'fw-bold text-primary' }
      }
    }),
    columnHelper.accessor('inversionNeta', {
      header: 'Inversión Neta',
      cell: info => {
        const row = info.row.original;
        return formatCurrency(info.getValue(), row.moneda);
      },
      meta: {
        headerProps: { style: { width: '140px' } },
        cellProps: { className: 'fw-bold text-success' }
      }
    }),
    columnHelper.accessor('status', {
      header: 'Estado',
      cell: info => (
        <Badge bg={info.getValue() === 'Activa' ? 'success' : 'danger'} className="fs-10">
          {info.getValue()}
        </Badge>
      ),
      meta: {
        headerProps: { style: { width: '100px' } }
      }
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Acciones',
      cell: info => {
        const order = info.row.original;
        return (
          <div className="d-flex gap-1">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleOrderSelect(order);
              }}
              title="Asignar Presupuesto"
            >
              <FontAwesomeIcon icon="chart-line" />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleShowOrderHistory(order);
              }}
              title="Ver Historial"
            >
              <FontAwesomeIcon icon="history" />
            </Button>
            <Button
              variant="outline-info"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(order);
              }}
              title="Editar"
            >
              <FontAwesomeIcon icon="edit" />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(order);
              }}
              title="Eliminar"
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '150px' } }
      }
    })
  ];

  // Memorizar las columnas para evitar re-renders
  const memoizedColumns = useMemo(() => columns, []);

  // Hooks de exportación
  useTableExport();

  // Configuración de tabla avanzada para órdenes
  const ordersTable = useAdvanceTable({
    data: orders,
    columns: memoizedColumns,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  // Definir columnas para la tabla de asignaciones
  const assignmentColumns = [
    columnHelper.accessor('description', {
      header: 'Descripción',
      cell: info => (
        <div>
          <div className="fw-semibold">{info.getValue()}</div>
          {info.row.original.notes && (
            <small className="text-muted">{info.row.original.notes}</small>
          )}
        </div>
      ),
      meta: {
        headerProps: { style: { minWidth: '200px' } }
      }
    }),
    columnHelper.accessor('investmentType', {
      header: 'Tipo',
      cell: info => (
        <Badge bg="info" className="fs-11">
          {info.getValue()}
        </Badge>
      ),
      meta: {
        headerProps: { style: { width: '100px' } }
      }
    }),
    columnHelper.accessor('dateFrom', {
      header: 'Periodo',
      cell: info => (
        <small>
          <div>{formatDateTimeSafe(info.getValue())}</div>
          <div className="text-muted">hasta</div>
          <div>{formatDateTimeSafe(info.row.original.dateTo)}</div>
        </small>
      ),
      meta: {
        headerProps: { style: { width: '120px' } }
      }
    }),
    columnHelper.accessor('amount', {
      header: 'Monto',
      cell: info => {
        const assignment = info.row.original;
        let displayAmount = parseFloat(assignment.montoValor || assignment.amount) || 0;
        
        // LÓGICA: Si es porcentaje, convertir a dólares
        const isPercentage = assignment.amountType === 'percentage' || 
                           (!assignment.amountType && displayAmount <= 100 && displayAmount > 0);
        
        if (isPercentage && selectedOrder?.inversionNeta) {
          // inversionNeta ya está parseado como número
          displayAmount = (displayAmount * selectedOrder.inversionNeta) / 100;
        } else if (isPercentage && selectedOrder?.netInvestment) {
          // Backup: usar netInvestment si inversionNeta no existe
          displayAmount = (displayAmount * parseFloat(selectedOrder.netInvestment)) / 100;
        }
        
        return (
          <div className="fw-semibold text-success">
            {formatCurrency(displayAmount)}
            {isPercentage && (
              <small className="text-muted d-block">
                ({assignment.montoValor || assignment.amount}%)
              </small>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '120px' } }
      }
    }),
    columnHelper.accessor('daysPerWeek', {
      header: 'Días/Sem',
      cell: info => (
        <Badge bg="secondary" className="fs-11 text-center">
          {info.getValue()}
        </Badge>
      ),
      meta: {
        headerProps: { style: { width: '80px' } }
      }
    }),
    columnHelper.accessor('dailyAmount', {
      header: 'Monto $ Ref. Diario',
      cell: info => (
        <small className="text-info">
          {formatCurrency(info.getValue())}
        </small>
      ),
      meta: {
        headerProps: { style: { width: '120px' } }
      }
    }),
    columnHelper.accessor('weeklyAmount', {
      header: 'Monto $ Ref. Semanal',
      cell: info => (
        <small className="text-warning">
          {formatCurrency(info.getValue())}
        </small>
      ),
      meta: {
        headerProps: { style: { width: '120px' } }
      }
    }),
    columnHelper.display({
      id: 'info-diaria',
      header: 'Info Diaria',
      cell: info => {
        const assignment = info.row.original;
        return (
          <DailyInfoTooltip assignment={assignment} formatCurrency={formatCurrency} />
        );
      },
      meta: {
        headerProps: { style: { width: '80px' } }
      }
    }),
    columnHelper.display({
      id: 'info-semanal',
      header: 'Info Semanal',
      cell: info => {
        const assignment = info.row.original;
        return (
          <WeeklyInfoTooltip assignment={assignment} formatCurrency={formatCurrency} />
        );
      },
      meta: {
        headerProps: { style: { width: '80px' } }
      }
    }),
    columnHelper.display({
      id: 'info-desloze-diario',
      header: 'Info Desgloze Diario',
      cell: info => {
        const assignment = info.row.original;
        return (
          <DailyBreakdownTooltip assignment={assignment} formatCurrency={formatCurrency} />
        );
      },
      meta: {
        headerProps: { style: { width: '140px' } }
      }
    }),
    columnHelper.accessor('status', {
      header: 'Estado',
      cell: info => (
        <Badge 
          bg={info.getValue() === 'active' ? 'success' : 'secondary'} 
          className="fs-11"
        >
          <FontAwesomeIcon 
            icon={info.getValue() === 'active' ? 'check-circle' : 'pause-circle'} 
            className="me-1" 
          />
          {info.getValue()}
        </Badge>
      ),
      meta: {
        headerProps: { style: { width: '100px' } }
      }
    }),
    columnHelper.display({
      id: 'assignment-actions',
      header: 'Acciones',
      cell: info => {
        const assignment = info.row.original;
        return (
          <div className="d-flex gap-1">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleShowAssignmentHistory(assignment);
              }}
              title="Ver Historial"
            >
              <FontAwesomeIcon icon="history" />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleAssignmentSelect(assignment);
              }}
              title="Editar asignación"
            >
              <FontAwesomeIcon icon="edit" />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteAssignment(assignment);
              }}
              title="Eliminar asignación"
              disabled={deletingAssignment === assignment.id}
            >
              {deletingAssignment === assignment.id ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <FontAwesomeIcon icon="trash" />
              )}
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '120px' } }
      }
    })
  ];

  // Memorizar las columnas de asignaciones
  const memoizedAssignmentColumns = useMemo(() => assignmentColumns, [selectedOrder]);

  // Configuración de tabla avanzada para asignaciones
  const assignmentsTable = useAdvanceTable({
    data: assignments,
    columns: memoizedAssignmentColumns,
    sortable: true,
    selection: false
  });

  // Configuración de React Table

  

  // Funciones para el modal de Fee Admin

  const handleCloseAdminFeeModal = () => {
    setShowAdminFeeModal(false);
  };

  const handleSelectAdminFee = (feeConfig) => {
    setSelectedAdminFee(feeConfig);
    
    // Aplicar la configuración seleccionada al formulario
    handleInputChange('feeAdminType', feeConfig.feeType);
    handleInputChange('feeAdmin', feeConfig.feeValue.toString());
    
    // Si hay un wizard activo, también actualizar sus datos
    if (window.wizardContext) {
      window.wizardContext.updateOrderData('feeAdminType', feeConfig.feeType);
      window.wizardContext.updateOrderData('feeAdmin', feeConfig.feeValue.toString());
    }
    
    // Cerrar el modal
    setShowAdminFeeModal(false);
    
    toast.success(`Fee Admin aplicado: ${feeConfig.name}`);
  };

  // Funciones para el modal de cálculo presupuestario

  const handleCloseCalculationModal = () => {
    setShowCalculationModal(false);
    setSelectedCalculationAssignment(null);
    setCalculationDistributionInfo(null);
  };

  // Funciones para el modal de historial
  const handleShowOrderHistory = (order) => {
    // Primero cerrar el modal si está abierto
    setShowHistoryModal(false);
    
    // Luego establecer los nuevos valores
    setHistoryEntityType('INSERTION_ORDER');
    setHistoryEntityId(order.id);
    setHistoryEntityName(`Orden #${order.id} - ${order.cliente}`);
    
    // Abrir el modal con un pequeño delay
    setTimeout(() => {
      setShowHistoryModal(true);
    }, 50);
  };

  const handleShowAssignmentHistory = (assignment) => {
    // Primero cerrar el modal si está abierto
    setShowHistoryModal(false);
    
    // Luego establecer los nuevos valores
    setHistoryEntityType('BUDGET_ASSIGNMENT');
    setHistoryEntityId(assignment.id);
    setHistoryEntityName(`Asignación #${assignment.id} - ${assignment.description}`);
    
    // Abrir el modal con un pequeño delay para asegurar que el estado se actualice
    setTimeout(() => {
      setShowHistoryModal(true);
    }, 50);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setHistoryEntityType(null);
    setHistoryEntityId(null);
    setHistoryEntityName('');
  };

  // Función para manejar el cierre del modal
  const handleCloseModal = () => {
    setShowNewOrderModal(false);
    setEditingOrder(null);
    resetForm();
  };

  // Función para manejar el envío desde el wizard
  const handleWizardSubmit = async (wizardData) => {
    setSaving(true);
    
    try {
      // El servicio se encarga de formatear los datos para la API
      let result;
      if (editingOrder) {
        result = await budgetService.updateInsertionOrder(editingOrder.id, wizardData);
      } else {
        result = await budgetService.createInsertionOrder(wizardData);
      }

      if (result.success) {
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
        await loadInsertionOrders();
        setShowNewOrderModal(false);
        resetForm();
        
        toast.success(
          editingOrder ? '¡Orden actualizada exitosamente!' : '¡Orden creada exitosamente!',
          { position: 'top-right' }
        );
      } else {
        toast.error(
          `Error al ${editingOrder ? 'actualizar' : 'crear'} la orden: ${result.error}`,
          { position: 'top-right' }
        );
      }
    } catch (error) {
      toast.error(
        `Error inesperado al ${editingOrder ? 'actualizar' : 'crear'} la orden`,
        { position: 'top-right' }
      );
    } finally {
      setSaving(false);
    }
  };

  // OPTIMIZACIÓN: Función para manejar el cambio de cliente en el wizard
  const handleWizardClientChange = (clientId) => {
    // COMENTADO: Ya no cargar cuentas automáticamente al seleccionar cliente
    // Solo cargar cuando se seleccione el nivel de asignación en AdvancedConfigurationStep
    // if (clientId) {
    //   loadAccountsByClient(clientId);
    // } else {
    //   setAvailableAccounts([]);
    // }
  };

  // Función para abrir el modal de admin fee desde el wizard
  const handleWizardOpenAdminFee = () => {
    setShowAdminFeeModal(true);
  };

  // Funciones para exportar reportes
  const exportAssignmentsToCSV = (assignments, selectedOrder) => {
    if (!assignments || assignments.length === 0) {
      toast.warn('No hay asignaciones para exportar');
      return;
    }

    const headers = [
      'ID',
      'Descripción',
      'Tipo Inversión',
      'Fecha Desde',
      'Fecha Hasta',
      'Monto Original',
      'Tipo Monto',
      'Monto en USD',
      'Días/Semana',
      'Monto Diario',
      'Monto Semanal',
      'Total Días',
      'Días Efectivos',
      'Estado',
      'Notas'
    ];

    const csvData = assignments.map(assignment => {
      const isPercentage = assignment.amountType === 'percentage' || 
                          (!assignment.amountType && parseFloat(assignment.montoValor) <= 100);
      
      let realAmount = parseFloat(assignment.montoValor) || 0;
      if (isPercentage && selectedOrder?.inversionNeta) {
        realAmount = (realAmount * parseFloat(selectedOrder.inversionNeta)) / 100;
      }

      return [
        assignment.id,
        assignment.description,
        assignment.investmentType,
        formatDateSafe(assignment.dateFrom),
        formatDateSafe(assignment.dateTo),
        assignment.montoValor,
        isPercentage ? 'Porcentaje' : 'USD Fijo',
        realAmount.toFixed(2),
        assignment.daysPerWeek,
        assignment.dailyAmount.toFixed(2),
        assignment.weeklyAmount.toFixed(2),
        assignment.distributionInfo?.totalDays || 0,
        Math.ceil((assignment.distributionInfo?.totalDays || 0) * (assignment.daysPerWeek || 7) / 7),
        assignment.status === 'active' ? 'Activo' : 'Inactivo',
        assignment.notes || ''
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `asignaciones_orden_${selectedOrder?.id || 'export'}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Reporte CSV exportado exitosamente');
  };

  const exportAssignmentsToPDF = async (assignments, selectedOrder) => {
    if (!assignments || assignments.length === 0) {
      toast.warn('No hay asignaciones para exportar');
      return;
    }

    try {
      // Crear contenido HTML para el PDF
      const htmlContent = `
        <html>
          <head>
            <title>Reporte de Asignaciones - Orden ${selectedOrder?.id}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; }
              .order-info { background: #f8f9fa; padding: 15px; margin-bottom: 20px; border-radius: 5px; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
              th { background-color: #007bff; color: white; }
              .amount { text-align: right; }
              .status-active { color: #28a745; font-weight: bold; }
              .status-inactive { color: #6c757d; }
              .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #666; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Reporte de Asignaciones Presupuestarias</h1>
              <h2>Orden de Inserción #${selectedOrder?.id}</h2>
              <p>Generado el: ${new Date().toLocaleString()}</p>
            </div>
            
            <div class="order-info">
              <h3>Información de la Orden</h3>
              <p><strong>Cliente:</strong> ${selectedOrder?.clientName || 'N/A'}</p>
              <p><strong>Business Unit:</strong> ${selectedOrder?.businessUnit || 'N/A'}</p>
              <p><strong>Descripción:</strong> ${selectedOrder?.description || 'N/A'}</p>
              <p><strong>Período:</strong> ${formatDateSafe(selectedOrder?.dateFrom)} - ${formatDateSafe(selectedOrder?.dateTo)}</p>
              <p><strong>Inversión Bruta:</strong> $${parseFloat(selectedOrder?.grossInvestment || 0).toLocaleString()}</p>
              <p><strong>Inversión Neta:</strong> $${parseFloat(selectedOrder?.netInvestment || 0).toLocaleString()}</p>
            </div>

            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Descripción</th>
                  <th>Tipo</th>
                  <th>Período</th>
                  <th>Monto Original</th>
                  <th>Monto USD</th>
                  <th>$/Día</th>
                  <th>$/Semana</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                ${assignments.map(assignment => {
                  const isPercentage = assignment.amountType === 'percentage' || 
                                     (!assignment.amountType && parseFloat(assignment.montoValor) <= 100);
                  
                  let realAmount = parseFloat(assignment.montoValor) || 0;
                  if (isPercentage && selectedOrder?.inversionNeta) {
                    realAmount = (realAmount * parseFloat(selectedOrder.inversionNeta)) / 100;
                  }

                  return `
                    <tr>
                      <td>${assignment.id}</td>
                      <td>${assignment.description}</td>
                      <td>${assignment.investmentType}</td>
                      <td>${formatDateSafe(assignment.dateFrom)} - ${formatDateSafe(assignment.dateTo)}</td>
                      <td class="amount">${assignment.montoValor}${isPercentage ? '%' : ' USD'}</td>
                      <td class="amount">$${realAmount.toLocaleString()}</td>
                      <td class="amount">$${assignment.dailyAmount.toLocaleString()}</td>
                      <td class="amount">$${assignment.weeklyAmount.toLocaleString()}</td>
                      <td class="${assignment.status === 'active' ? 'status-active' : 'status-inactive'}">
                        ${assignment.status === 'active' ? 'Activo' : 'Inactivo'}
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>

            <div class="footer">
              <p>Reporte generado por GalaxyMetrics - Sistema de Control Presupuestario</p>
            </div>
          </body>
        </html>
      `;

      // Abrir ventana de impresión
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      
      toast.success('Reporte PDF generado para impresión');
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar el reporte PDF');
    }
  };

  // Función para cargar actividad reciente del sistema de logging

  // Debug: Monitorear cambios en el estado del modal de historial
  useEffect(() => {
    // Debug logs removed for production
  }, [showHistoryModal, historyEntityType, historyEntityId, historyEntityName]);

  // *** NUEVA FUNCIÓN: Invalidar caché de cuentas ***
  const invalidateAccountsCache = useCallback((clientId = null) => {
    if (clientId) {
      // Invalidar caché de un cliente específico
      setAccountsCache(prev => {
        const newCache = { ...prev };
        delete newCache[clientId];
        return newCache;
      });
      
    } else {
      // Invalidar todo el caché
      setAccountsCache({});
      
    }
  }, []);

  // *** NUEVA FUNCIÓN: Forzar recarga de cuentas ***
  const forceReloadAccounts = useCallback(async (clientId) => {
    if (!clientId) return;
    
    
    
    // Invalidar caché
    invalidateAccountsCache(clientId);
    
    // Recargar inmediatamente
    setLoadingAccounts(true);
    setLoadingAccountsFor(clientId);
    
    try {
      const result = await budgetService.getAccountsByClient(clientId);
      
      if (result.success) {
        const accounts = result.accounts || [];
        setAvailableAccounts(accounts);
        
        // Actualizar caché con datos frescos
        setAccountsCache(prev => ({
          ...prev,
          [clientId]: accounts
        }));
        

      } else {
        console.error('Error reloading accounts:', result.error);
        setAvailableAccounts([]);
      }
    } catch (error) {
      console.error('Error reloading accounts:', error);
      setAvailableAccounts([]);
    } finally {
      setLoadingAccounts(false);
      setLoadingAccountsFor(null);
    }
  }, [invalidateAccountsCache]);

  // *** EXPONER FUNCIÓN DE RECARGA PARA USO EXTERNO ***
  // Esta función puede ser llamada desde componentes de gestión de cuentas
  useEffect(() => {
    // Registrar la función globalmente para que otros componentes puedan invalidar el caché
    window.refreshAccountsCache = forceReloadAccounts;
    
    return () => {
      delete window.refreshAccountsCache;
    };
  }, [forceReloadAccounts]);

  return (
    <Container fluid className="px-3">
      {/* Estilos CSS para Timeline */}
      <style>{timelineStyles}</style>
      
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4 card card-body">
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-2">
                <FontAwesomeIcon 
                  icon={isBrandingSection ? "palette" : "bullseye"} 
                  className={`me-2 ${isBrandingSection ? 'text-info' : 'text-primary'}`} 
                />
                Gestión de Órdenes 
                <Badge 
                  bg={isBrandingSection ? "info" : "primary"} 
                  className="ms-2 fs-10"
                >
                  {campaignTypeFilter}
                </Badge>
              </h3>

            </Col>
            <Col xs="auto">
              <Button 
                variant="primary" 
                onClick={() => {
                  resetForm();
                  setShowNewOrderModal(true);
                }}
                className="me-2"
              >
                <FontAwesomeIcon icon="plus" className="me-1" />
                Nueva Orden
              </Button>
            </Col>
          </Row>
        </div>



        {/* Alert de éxito */}
        {showAlert && (
          <Alert variant="success" className="mb-4">
            <FontAwesomeIcon icon="check-circle" className="me-2" />
            {editingOrder ? '¡Orden actualizada exitosamente!' : '¡Orden creada exitosamente!'} Los datos han sido guardados.
          </Alert>
        )}

        {/* MASTER SECTION: Lista de Órdenes */}
        <Card className="shadow-sm mb-4">
          <FalconCardHeader title="Lista de Órdenes de Inserción" titleTag="h5" className="py-2">
            <Flex>
              <Badge bg="primary" className="fs-11 rounded-pill">
                <FontAwesomeIcon icon="list" className="me-1" />
                {orders.length} Órdenes
              </Badge>
              {selectedOrder && (
                <Badge bg="warning" className="fs-11 rounded-pill ms-2">
                  <FontAwesomeIcon icon="eye" className="me-1" />
                  ID: {selectedOrder.id} Seleccionada
                </Badge>
              )}
            </Flex>
          </FalconCardHeader>

          <Card.Body className="p-3">
            {orders.length > 0 ? (
              <>
                {/* Tabla Avanzada de Falcon */}
                <AdvanceTableProvider {...ordersTable}>
                  <div className="border-bottom border-200 mb-3">
                    <AdvanceTableSearchBox />
                  </div>
                  
                  <AdvanceTable
                    tableProps={{
                      className: 'table-hover fs-10 mb-0 clickable-rows',
                      size: 'sm'
                    }}
                    rowProps={(row) => ({
                      style: { cursor: 'pointer' },
                      title: 'Haz clic para ver las asignaciones de esta orden',
                      'data-row-index': row.index
                    })}
                    onRowClick={(row, e) => {
                      // Evitar activación si se hace clic en botones o elementos interactivos
                      if (e.target.closest('button') || e.target.closest('.btn') || e.target.closest('[role="button"]')) {
                        return;
                      }
                      handleOrderSelect(row.original);
                    }}
                  />
                  
                  <div className="d-flex flex-between-center pt-3">
                    <AdvanceTablePagination />
                  </div>
                </AdvanceTableProvider>
              </>
            ) : (
              <div className="text-center py-5">
                <FontAwesomeIcon icon="archive" className="fs-1 text-300 mb-3" />
                <h6 className="text-500">No hay órdenes creadas</h6>
                <p className="text-600 mb-4">
                  Comienza creando tu primera orden de inserción
                </p>
                <Button 
                  variant="primary" 
                  onClick={() => {
                    resetForm();
                    setShowNewOrderModal(true);
                  }}
                >
                  <FontAwesomeIcon icon="plus" className="me-1" />
                  Crear primera orden
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* DETAIL SECTION: Asignación de Presupuesto */}
        {selectedOrder && (
          <Card className="shadow-sm mb-4" data-assignment-section>
            <FalconCardHeader 
              title={`Asignación de Presupuesto - Orden ID: ${selectedOrder.id}`} 
              titleTag="h5" 
              className="py-2"
            >
              <Flex>
                <Badge bg="info" className="fs-11 rounded-pill">
                  <FontAwesomeIcon icon="user" className="me-1" />
                  {selectedOrder.cliente}
                </Badge>
                <Badge bg="secondary" className="fs-11 rounded-pill ms-2">
                  <FontAwesomeIcon icon="chart-bar" className="me-1" />
                  {selectedOrder.businessUnit}
                </Badge>
                <Badge bg="success" className="fs-11 rounded-pill ms-2">
                  <FontAwesomeIcon icon="dollar-sign" className="me-1" />
                  {formatCurrency(selectedOrder.inversionBruta)}
                </Badge>
              </Flex>
            </FalconCardHeader>
            
            <Card.Body className="p-0">
              <Tab.Container activeKey={assignmentActiveTab} onSelect={setAssignmentActiveTab}>
                <Nav variant="tabs" className="border-bottom">
                  <Nav.Item>
                    <Nav.Link eventKey="summary" className="px-4 py-3">
                      <FontAwesomeIcon icon="chart-pie" className="me-2" />
                      Resumen & Distribución
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="new-assignment" className="px-4 py-3">
                      <FontAwesomeIcon icon="plus-circle" className="me-2" />
                      Nueva Asignación
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link eventKey="history" className="px-4 py-3">
                      <FontAwesomeIcon icon="history" className="me-2" />
                      Historial & Modificaciones
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  {/* Tab 1: Resumen & Distribución */}
                  <Tab.Pane eventKey="summary">
                    <div className="p-4">
                      {/* Información de la orden seleccionada - COMPACTO & RESPONSIVE */}
                      <Row className="mb-4">
                        <Col lg={8}>
                          <Card className="overflow-hidden shadow-sm h-100">
                            {/* Header compacto con información clave */}
                            <div className="bg-primary text-white p-2 px-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <div className="bg-white text-primary rounded-circle me-2 d-flex align-items-center justify-content-center" style={{width: '28px', height: '28px'}}>
                                    <FontAwesomeIcon icon="file-alt" className="fs-8" />
                                  </div>
                                  <div>
                                    <h6 className="text-white mb-0 fw-bold fs-10">Orden #{selectedOrder.id}</h6>
                                    <small className="text-white opacity-75 fs-11">
                                      {formatDateTimeSafe(selectedOrder.fechaDesde)}
                                    </small>
                                  </div>
                                </div>
                                <div className="text-end">
                                  <div className="text-white fs-8 fw-bold">{formatCurrency(selectedOrder.inversionBruta)}</div>
                                  <small className="text-white opacity-75 fs-11">Inversión</small>
                                </div>
                              </div>
                            </div>

                            <Card.Body className="p-0">
                              <Row className="g-0">
                                {/* Fila 1: Cliente y Business Unit */}
                                <Col xs={6} sm={6} md={6} className="border-end border-200 border-bottom border-200">
                                  <div className="p-3 text-center">
                                    <div className="icon-circle icon-circle-primary mb-2 mx-auto" style={{width: '35px', height: '35px'}}>
                                      <FontAwesomeIcon icon="user" className="fs-7 text-primary" />
                                    </div>
                                    <h6 className="text-600 mb-1 fs-11 text-uppercase fw-semibold">Cliente</h6>
                                    <div className="text-700 fw-medium fs-9">{selectedOrder.cliente}</div>
                                  </div>
                                </Col>
                                
                                <Col xs={6} sm={6} md={6} className="border-bottom border-200">
                                  <div className="p-3 text-center">
                                    <div className="icon-circle icon-circle-info mb-2 mx-auto" style={{width: '35px', height: '35px'}}>
                                      <FontAwesomeIcon icon="cog" className="fs-7 text-info" />
                                    </div>
                                    <h6 className="text-600 mb-1 fs-11 text-uppercase fw-semibold">Business Unit</h6>
                                    <div className="text-700 fw-medium fs-9">{selectedOrder.businessUnit}</div>
                                  </div>
                                </Col>
                                
                                {/* Fila 2: Período y Cuenta */}
                                <Col xs={6} sm={6} md={6} className="border-end border-200">
                                  <div className="p-3 text-center">
                                    <div className="icon-circle icon-circle-success mb-2 mx-auto" style={{width: '35px', height: '35px'}}>
                                      <FontAwesomeIcon icon="calendar" className="fs-7 text-success" />
                                    </div>
                                    <h6 className="text-600 mb-1 fs-11 text-uppercase fw-semibold">Período</h6>
                                    <div className="text-700 fw-medium fs-10">
                                      <div className="fs-11">{formatDateTimeSafe(selectedOrder.fechaDesde)}</div>
                                      <div className="text-muted fs-11">hasta</div>
                                      <div className="fs-11">{formatDateTimeSafe(selectedOrder.fechaHasta)}</div>
                                    </div>
                                  </div>
                                </Col>
                                
                                <Col xs={6} sm={6} md={6}>
                                  <div className="p-3 text-center">
                                    <div className="icon-circle icon-circle-secondary mb-2 mx-auto" style={{width: '35px', height: '35px'}}>
                                      <FontAwesomeIcon icon="user-circle" className="fs-7 text-secondary" />
                                    </div>
                                    <h6 className="text-600 mb-1 fs-11 text-uppercase fw-semibold">Cuenta</h6>
                                    <div className="text-700 fw-medium fs-10">
                                      <div className="fs-11">{renderAccountInfo(selectedOrder)}</div>
                                      <small className="text-muted fs-11">(Meta)</small>
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                              
                              {/* Descripción - Fila completa pero compacta */}
                              <div className="border-top border-200 p-3 bg-light">
                                <div className="d-flex align-items-center">
                                  <div className="icon-circle icon-circle-warning me-3" style={{width: '30px', height: '30px'}}>
                                    <FontAwesomeIcon icon="align-left" className="fs-8 text-warning" />
                                  </div>
                                  <div className="flex-1">
                                    <h6 className="text-600 mb-1 fs-11 text-uppercase fw-semibold">Descripción</h6>
                                    <p className="mb-0 fs-10 text-700">{selectedOrder.descripcion}</p>
                                  </div>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col lg={4}>
                          {(() => {
                            // Calcular total asignado considerando tipos de monto (porcentaje vs fijo)
                            const totalAsignado = assignments.reduce((sum, a) => {
                              let amount = parseFloat(a.montoValor || a.amount) || 0;
                              
                              // LÓGICA TEMPORAL: Si el backend envía amountType, usarlo
                              // Si no, detectar si es porcentaje (valor <= 100)
                              const isPercentage = a.amountType === 'percentage' || 
                                                 (!a.amountType && amount <= 100 && amount > 0);
                              
                              // Si es porcentaje, convertir a dólares basado en la inversión neta
                              if (isPercentage && selectedOrder?.inversionNeta) {
                                amount = (amount * parseFloat(selectedOrder.inversionNeta)) / 100;
                              }
                              return sum + amount;
                            }, 0);
                            
                            const inversionNeta = parseFloat(selectedOrder.inversionNeta) || 0;
                            const disponible = inversionNeta - totalAsignado;
                            const porcentajeAsignado = inversionNeta > 0 ? (totalAsignado / inversionNeta) * 100 : 0;
                            const excedido = disponible < 0;
                            
                            return (
                              <Card className={`border-0 h-100 ${excedido ? 'bg-danger' : 'bg-primary'} text-white`}>
                                <Card.Body className="text-center">
                                  <h6 className="mb-3 text-white">
                                    <FontAwesomeIcon icon="chart-pie" className="me-2" />
                                    Resumen Financiero
                                  </h6>
                                  
                                  <div className="mb-3">
                                    <div className="fs-4 fw-bold">{formatCurrency(selectedOrder.inversionBruta)}</div>
                                    <small className="text-100">Inversión Bruta</small>
                                  </div>
                                  
                                  <div className="mb-3">
                                    <div className="fs-5 fw-semibold">{formatCurrency(inversionNeta)}</div>
                                    <small className="text-100">Inversión Neta</small>
                                  </div>
                                  
                                  <div className="mb-3">
                                    <div className="fs-5 fw-semibold">{formatCurrency(totalAsignado)}</div>
                                    <small className="text-100">Total Asignado</small>
                                  </div>
                                  
                                  <hr className="my-3 border-light" />
                                  
                                  <div className="mb-2">
                                    <div className={`fs-4 fw-bold ${excedido ? 'text-warning' : ''}`}>
                                      {formatCurrency(Math.abs(disponible))}
                                    </div>
                                    <small className="text-100">
                                      {excedido ? '⚠️ EXCESO PRESUPUESTO' : '✅ Disponible para Asignar'}
                                    </small>
                                  </div>
                                  
                                  <div>
                                    <div className="fs-6">{porcentajeAsignado.toFixed(1)}%</div>
                                    <small className="text-100">% Asignado</small>
                                  </div>
                                  
                                  {excedido && (
                                    <div className="mt-3 p-2 bg-warning text-dark rounded">
                                      <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                                      <small className="fw-bold">Presupuesto Excedido</small>
                                    </div>
                                  )}
                                </Card.Body>
                              </Card>
                            );
                          })()}
                        </Col>
                      </Row>

                      {/* Lista de asignaciones existentes */}
                      <div className="mb-4">
                        <h6 className="mb-3 text-700">
                          <FontAwesomeIcon icon="list-alt" className="me-2 text-success" />
                          Asignaciones Actuales ({assignments.length})
                        </h6>
                        
                        {loadingAssignments ? (
                          <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Cargando asignaciones...</span>
                            </div>
                          </div>
                                ) : assignments.length > 0 ? (
          <>
            {/* Controles de Exportación para Asignaciones */}
            <Row className="mb-3">
              <Col>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => exportAssignmentsToCSV(assignments, selectedOrder)}
                    disabled={!assignments || assignments.length === 0}
                  >
                    <FontAwesomeIcon icon="file-alt" className="me-1" />
                    Exportar CSV
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => exportAssignmentsToPDF(assignments, selectedOrder)}
                    disabled={!assignments || assignments.length === 0}
                  >
                    <FontAwesomeIcon icon="file-alt" className="me-1" />
                    Imprimir PDF
                  </Button>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => {
                      if (!assignments || assignments.length === 0) {
                        toast.warn('No hay asignaciones para copiar');
                        return;
                      }
                      
                      const textData = assignments.map(assignment => {
                        const isPercentage = assignment.amountType === 'percentage' || 
                                           (!assignment.amountType && parseFloat(assignment.montoValor) <= 100);
                        
                        let realAmount = parseFloat(assignment.montoValor) || 0;
                        if (isPercentage && selectedOrder?.inversionNeta) {
                          realAmount = (realAmount * parseFloat(selectedOrder.inversionNeta)) / 100;
                        }

                        return `ID: ${assignment.id} | ${assignment.description} | ${assignment.investmentType} | ${formatCurrency(realAmount)} | Diario: ${formatCurrency(assignment.dailyAmount)} | Semanal: ${formatCurrency(assignment.weeklyAmount)}`;
                      }).join('\n');
                      
                      navigator.clipboard.writeText(textData).then(() => {
                        toast.success('Datos copiados al portapapeles');
                      }).catch(() => {
                        toast.error('Error al copiar los datos');
                      });
                    }}
                    disabled={!assignments || assignments.length === 0}
                  >
                    <FontAwesomeIcon icon="copy" className="me-1" />
                    Copiar Datos
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Tabla Avanzada de Asignaciones */}
            <div data-assignment-section>
            <AdvanceTableProvider {...assignmentsTable}>
              <AdvanceTable
                tableProps={{
                  className: 'table-hover fs-10 mb-0 clickable-rows',
                  size: 'sm'
                }}
                rowProps={(row) => ({
                  style: { cursor: 'pointer' },
                  title: 'Haz clic para editar esta asignación',
                  'data-row-index': row.index
                })}
                onRowClick={(row, e) => {
                  // Evitar activación si se hace clic en botones o elementos interactivos
                  if (e.target.closest('button') || e.target.closest('.btn') || e.target.closest('[role="button"]')) {
                    return;
                  }
                  handleAssignmentSelect(row.original);
                }}
              />
            </AdvanceTableProvider>
            </div>
          </>
        ) : (
                          <div className="text-center py-4">
                            <FontAwesomeIcon icon="archive" className="fs-1 text-300 mb-3" />
                            <h6 className="text-600 mb-2">No hay asignaciones creadas</h6>
                            <p className="text-400 mb-3">Comienza creando tu primera asignación de presupuesto</p>
                            <Button 
                              variant="primary" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssignmentActiveTab('new-assignment');
                              }}
                            >
                              <FontAwesomeIcon icon="plus" className="me-1" />
                              Nueva Asignación
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Tab.Pane>

                  {/* Tab 2: Nueva Asignación */}
                  <Tab.Pane eventKey="new-assignment">
                    <div className="p-4">
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-3 text-700">
                              <FontAwesomeIcon icon="plus-circle" className="me-2 text-primary" />
                              {editingAssignment ? 'Editar Asignación' : 'Nueva Asignación de Presupuesto'}
                            </h6>
                            <p className="text-600 mb-0">
                              Configure los detalles de la asignación para la orden seleccionada
                            </p>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignmentActiveTab('summary');
                            }}
                          >
                            <FontAwesomeIcon icon="list" className="me-1" />
                            Ver Resumen ({assignments.length})
                          </Button>
                        </div>
                      </div>

                      <Form>
                        <Row className="g-4">
                          <Col lg={8}>
                            <Card className="border-0 bg-light">
                              <Card.Body>
                                <Row className="g-3">
                                  <Col xs={12}>
                                    <Form.Group>
                                      <Form.Label className="fw-semibold">
                                        <FontAwesomeIcon icon="edit" className="me-1 text-success" />
                                        Descripción de la Asignación *
                                      </Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={assignmentFormData.description}
                                        onChange={(e) => handleAssignmentInputChange('description', e.target.value)}
                                        placeholder="Ej: Meta Awareness Campaign - Enero 2024"
                                        required
                                      />
                                      <Form.Text className="text-muted">
                                        Describe claramente esta asignación específica de presupuesto
                                      </Form.Text>
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label className="fw-semibold">
                                        <FontAwesomeIcon icon="chart-bar" className="me-1 text-info" />
                                        Tipo de Inversión *
                                      </Form.Label>
                                      <Form.Select
                                        value={assignmentFormData.investmentType}
                                        onChange={(e) => handleAssignmentInputChange('investmentType', e.target.value)}
                                        required
                                      >
                                        {investmentTypes.map(type => (
                                          <option key={type} value={type}>{type}</option>
                                        ))}
                                      </Form.Select>
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label className="fw-semibold">
                                        <FontAwesomeIcon icon="dollar-sign" className="me-1 text-success" />
                                        Monto de Asignación *
                                      </Form.Label>
                                      <InputGroup>
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          max={assignmentFormData.amountType === 'percentage' ? 100 : undefined}
                                          value={assignmentFormData.amount}
                                          onChange={(e) => handleAssignmentInputChange('amount', e.target.value)}
                                          placeholder={assignmentFormData.amountType === 'percentage' ? '25.5' : '1000.00'}
                                          required
                                          isInvalid={assignmentFormData.amountType === 'percentage' && parseFloat(assignmentFormData.amount) > 100}
                                        />
                                        {assignmentFormData.amountType === 'percentage' && parseFloat(assignmentFormData.amount) > 100 && (
                                          <Form.Control.Feedback type="invalid">
                                            El porcentaje no puede ser mayor al 100%
                                          </Form.Control.Feedback>
                                        )}
                                        <Form.Select
                                          value={assignmentFormData.amountType}
                                          onChange={(e) => handleAssignmentInputChange('amountType', e.target.value)}
                                          style={{ maxWidth: '100px' }}
                                        >
                                          <option value="fixed">USD</option>
                                          <option value="percentage">%</option>
                                        </Form.Select>
                                      </InputGroup>
                                      <Form.Text className="text-muted">
                                        {assignmentFormData.amountType === 'percentage' 
                                          ? 'Porcentaje de la inversión neta de la orden'
                                          : 'Monto fijo en dólares americanos'
                                        }
                                      </Form.Text>
                                      {assignmentFormData.amountType === 'percentage' && assignmentFormData.amount && selectedOrder?.inversionNeta && (
                                        <div className="mt-1">
                                          <small className="text-success fw-bold">
                                            = {formatCurrency((parseFloat(assignmentFormData.amount) * parseFloat(selectedOrder.inversionNeta)) / 100)}
                                          </small>
                                        </div>
                                      )}
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label className="fw-semibold">
                                        <FontAwesomeIcon icon="calendar-alt" className="me-1 text-warning" />
                                        Fecha y Hora de Inicio *
                                      </Form.Label>
                                      <DatePicker
                                        selected={assignmentFormData.dateFrom}
                                        onChange={(date) => handleAssignmentInputChange('dateFrom', date)}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={1}
                                        timeCaption="Hora"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        className="form-control"
                                        placeholderText="Seleccionar fecha y hora..."
                                        minDate={selectedOrder?.fechaDesde ? new Date(selectedOrder.fechaDesde) : null}
                                        maxDate={selectedOrder?.fechaHasta ? new Date(selectedOrder.fechaHasta) : null}
                                        required
                                      />
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label className="fw-semibold">
                                        <FontAwesomeIcon icon="calendar-check" className="me-1 text-danger" />
                                        Fecha y Hora de Fin *
                                      </Form.Label>
                                      <DatePicker
                                        selected={assignmentFormData.dateTo}
                                        onChange={(date) => handleAssignmentInputChange('dateTo', date)}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={1}
                                        timeCaption="Hora"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        className="form-control"
                                        placeholderText="Seleccionar fecha y hora..."
                                        minDate={assignmentFormData.dateFrom || (selectedOrder?.fechaDesde ? new Date(selectedOrder.fechaDesde) : null)}
                                        maxDate={selectedOrder?.fechaHasta ? new Date(selectedOrder.fechaHasta) : null}
                                        required
                                      />
                                    </Form.Group>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Group>
                                      <Form.Label className="fw-semibold">
                                        <FontAwesomeIcon icon="calendar-week" className="me-1 text-secondary" />
                                        Días por Semana
                                      </Form.Label>
                                      <Form.Select
                                        value={assignmentFormData.daysPerWeek}
                                        onChange={(e) => handleAssignmentInputChange('daysPerWeek', parseInt(e.target.value))}
                                      >
                                        <option value={1}>1 día (Lunes)</option>
                                        <option value={2}>2 días (Lun-Mar)</option>
                                        <option value={3}>3 días (Lun-Mié)</option>
                                        <option value={4}>4 días (Lun-Jue)</option>
                                        <option value={5}>5 días (Lun-Vie)</option>
                                        <option value={6}>6 días (Lun-Sáb)</option>
                                        <option value={7}>7 días (Todos)</option>
                                      </Form.Select>
                                    </Form.Group>
                                  </Col>

                                  <Col xs={12}>
                                    <Form.Group>
                                      <Form.Label className="fw-semibold">
                                        <FontAwesomeIcon icon="edit" className="me-1 text-warning" />
                                        Notas Adicionales
                                      </Form.Label>
                                      <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={assignmentFormData.notes}
                                        onChange={(e) => handleAssignmentInputChange('notes', e.target.value)}
                                        placeholder="Agrega cualquier información adicional sobre esta asignación..."
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col lg={4}>
                            <Card className="border-0 bg-primary text-white h-100">
                              <Card.Body>
                                <h6 className="mb-3 text-white">
                                  <FontAwesomeIcon icon="calculator" className="me-2" />
                                  Cálculos Automáticos
                                </h6>
                                
                                {/* Mostrar el monto en dólares si es porcentaje */}
                                {assignmentFormData.amountType === 'percentage' && assignmentFormData.amount && selectedOrder?.inversionNeta && (
                                  <div className="mb-3 p-2 bg-warning text-dark rounded">
                                    <small className="fw-bold d-block">Conversión de Porcentaje:</small>
                                    <div className="fs-6 fw-bold">
                                      {assignmentFormData.amount}% = {formatCurrency((parseFloat(assignmentFormData.amount) * parseFloat(selectedOrder.inversionNeta)) / 100)}
                                    </div>
                                  </div>
                                )}
                                
                                <div className="mb-3">
                                  <small className="text-100">Monto Diario:</small>
                                  <div className="fs-5 fw-semibold">
                                    ${assignmentFormData.dailyAmount || '0.00'}
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <small className="text-100">Monto Semanal:</small>
                                  <div className="fs-5 fw-semibold">
                                    ${assignmentFormData.weeklyAmount || '0.00'}
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <small className="text-100">Días por Semana:</small>
                                  <div className="fs-4 fw-bold">
                                    {assignmentFormData.daysPerWeek}
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <small className="text-100">Tipo de Inversión:</small>
                                  <div className="fs-6 fw-semibold">
                                    {assignmentFormData.investmentType}
                                  </div>
                                </div>

                                <hr className="border-light opacity-25" />

                                <div className="mb-3">
                                  <small className="text-100">Presupuesto Orden:</small>
                                  <div className="fs-6 fw-semibold">
                                    {formatCurrency(selectedOrder?.inversionNeta || 0)}
                                  </div>
                                </div>

                                <div>
                                  <small className="text-100">Total Asignado:</small>
                                  <div className="fs-6 fw-semibold">
                                    {formatCurrency(assignments.reduce((sum, a) => {
                                      let amount = parseFloat(a.montoValor || a.amount) || 0;
                                      const isPercentage = a.amountType === 'percentage' || 
                                                         (!a.amountType && amount <= 100 && amount > 0);
                                      if (isPercentage && selectedOrder?.inversionNeta) {
                                        amount = (amount * parseFloat(selectedOrder.inversionNeta)) / 100;
                                      }
                                      return sum + amount;
                                    }, 0))}
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <small className="text-100">Disponible:</small>
                                  <div className="fs-5 fw-bold">
                                    {formatCurrency(
                                      (selectedOrder?.inversionNeta || 0) - 
                                      assignments.reduce((sum, a) => {
                                        let amount = parseFloat(a.montoValor || a.amount) || 0;
                                        const isPercentage = a.amountType === 'percentage' || 
                                                           (!a.amountType && amount <= 100 && amount > 0);
                                        if (isPercentage && selectedOrder?.inversionNeta) {
                                          amount = (amount * parseFloat(selectedOrder.inversionNeta)) / 100;
                                        }
                                        return sum + amount;
                                      }, 0)
                                    )}
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        <div className="mt-4 d-flex justify-content-between align-items-center">
                          <Button
                            variant="outline-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignmentActiveTab('summary');
                            }}
                          >
                            <FontAwesomeIcon icon="list" className="me-1" />
                            Ver Resumen
                          </Button>
                          
                          <div className="d-flex gap-2">
                            {/* Botón para limpiar solo el formulario */}
                            <Button
                              variant="outline-warning"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssignmentFormData({
                                  description: '',
                                  investmentType: 'CPM',
                                  dateFrom: null,
                                  dateTo: null,
                                  amount: '',
                                  amountType: 'fixed',
                                  daysPerWeek: 7,
                                  dailyAmount: '',
                                  weeklyAmount: '',
                                  campaignId: '',
                                  notes: ''
                                });
                                setEditingAssignment(null);
                                toast.info('📝 Formulario limpiado - Listo para nueva asignación');
                              }}
                              disabled={savingAssignment}
                            >
                              <FontAwesomeIcon icon="redo" className="me-1" />
                              Limpiar Formulario
                            </Button>
                            
                            {/* Botón principal de crear/actualizar */}
                            <Button
                              variant="primary"
                              disabled={savingAssignment}
                              onClick={(e) => {
                                e.stopPropagation();
                                
                                // VALIDACIONES LOCALES - Ahora cerca del formulario
                                if (!selectedOrder) {
                                  toast.error('❌ Debe seleccionar una orden primero');
                                  return;
                                }

                                // Validar campos obligatorios
                                if (!assignmentFormData.description || !assignmentFormData.amount || !assignmentFormData.dateFrom || !assignmentFormData.dateTo) {
                                  setValidationMessage({
                                    type: 'error',
                                    title: 'Error de Validación',
                                    text: 'Todos los campos marcados con * son obligatorios para crear la asignación.',
                                  });
                                  setShowValidationAlert(true);
                                  setTimeout(() => setShowValidationAlert(false), 8000);
                                  return;
                                }

                                if (assignmentFormData.amountType === 'percentage' && parseFloat(assignmentFormData.amount) > 100) {
                                  setValidationAlert({
                                    variant: 'danger',
                                    title: 'Error de Validación',
                                    text: 'El porcentaje no puede ser mayor al 100%. Por favor, ajusta el valor.',
                                  });
                                  setShowValidationAlert(true);
                                  setTimeout(() => setShowValidationAlert(false), 6000);
                                  return;
                                }

                                // Validar exceso de presupuesto (solo mostrar advertencia, no bloquear)
                                const budgetValidation = validateBudgetExcess(assignmentFormData.amount);
                                
                                if (!budgetValidation.isValid) {
                                  // Solo mostrar advertencia, pero permitir continuar
                                  toast.warn(`⚠️ Advertencia: Esta asignación excede el presupuesto por $${budgetValidation.exceedsBy.toFixed(2)}. Total disponible: $${budgetValidation.remainingBudget.toFixed(2)}`, {
                                    autoClose: 8000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                  });
                                }

                                // Ejecutar la función (sin bloquear por presupuesto)
                                handleCreateAssignment();
                              }}
                            >
                              {savingAssignment ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  {editingAssignment ? 'Actualizando...' : 'Guardando...'}
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon="check" className="me-1" />
                                  {editingAssignment ? 'Actualizar Asignación' : 'Crear Asignación'}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  </Tab.Pane>

                  {/* Tab 3: Historial & Modificaciones */}
                  <Tab.Pane eventKey="history">
                    <div className="p-4">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                          <h6 className="mb-1 text-700">
                            <FontAwesomeIcon icon="history" className="me-2 text-info" />
                            Historial de Modificaciones
                          </h6>
                          <p className="text-600 mb-0 fs-11">
                            Registro completo de cambios realizados en la orden y sus asignaciones
                          </p>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowOrderHistory(selectedOrder);
                            }}
                          >
                            <FontAwesomeIcon icon="file-alt" className="me-1" />
                            Ver Historial de Orden
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignmentActiveTab('summary');
                            }}
                          >
                            <FontAwesomeIcon icon="arrow-left" className="me-1" />
                            Volver al Resumen
                          </Button>
                        </div>
                      </div>

                      <Row>
                        <Col lg={6}>
                          <Card className="h-100 border-primary">
                            <Card.Header className="bg-primary text-white py-2">
                              <h6 className="mb-0">
                                <FontAwesomeIcon icon="file-contract" className="me-2" />
                                Actividad de la Orden
                              </h6>
                            </Card.Header>
                            <Card.Body className="p-3">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary rounded-circle p-2 me-3">
                                    <FontAwesomeIcon icon="info-circle" className="text-white fs-8" />
                                  </div>
                                  <div>
                                    <div className="fw-semibold fs-10">Orden #{selectedOrder.id}</div>
                                    <small className="text-muted">{selectedOrder.cliente}</small>
                                  </div>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowOrderHistory(selectedOrder);
                                  }}
                                >
                                  <FontAwesomeIcon icon="external-link-alt" className="me-1" />
                                  Ver Completo
                                </Button>
                              </div>
                              
                              <div className="timeline-simple">
                                <div className="timeline-item">
                                  <div className="timeline-marker bg-success"></div>
                                  <div className="timeline-content">
                                    <div className="fs-11 fw-semibold">Orden Creada</div>
                                    <div className="fs-11 text-muted">
                                      {selectedOrder.fechaCreacion ? 
                                        formatDateSafe(selectedOrder.fechaCreacion) : 
                                        'Fecha no disponible'
                                      }
                                    </div>
                                    <small className="text-success">Sistema</small>
                                  </div>
                                </div>
                                
                                {selectedOrder.fechaModificacion && selectedOrder.fechaModificacion !== selectedOrder.fechaCreacion && (
                                  <div className="timeline-item">
                                    <div className="timeline-marker bg-warning"></div>
                                    <div className="timeline-content">
                                      <div className="fs-11 fw-semibold">Última Modificación</div>
                                      <div className="fs-11 text-muted">
                                        {formatDateSafe(selectedOrder.fechaModificacion)}
                                      </div>
                                      <small className="text-warning">Usuario</small>
                                    </div>
                                  </div>
                                )}
                                
                                <div className="timeline-item">
                                  <div className="timeline-marker bg-info"></div>
                                  <div className="timeline-content">
                                    <div className="fs-11 fw-semibold">Estado Actual</div>
                                    <div className="fs-11 text-muted">
                                      <Badge bg={selectedOrder.status === 'Activa' ? 'success' : 'secondary'}>
                                        {selectedOrder.status || 'Activa'}
                                      </Badge>
                                    </div>
                                    <small className="text-info">Sistema</small>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mt-3 pt-3 border-top">
                                <div className="d-flex justify-content-between text-muted fs-11">
                                  <span>Inversión Total:</span>
                                  <span className="fw-semibold text-success">
                                    {formatCurrency(selectedOrder.inversionBruta)}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between text-muted fs-11">
                                  <span>Asignaciones:</span>
                                  <span className="fw-semibold text-primary">
                                    {assignments.length} registros
                                  </span>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                        
                        <Col lg={6}>
                          <Card className="h-100 border-success">
                            <Card.Header className="bg-success text-white py-2">
                              <h6 className="mb-0">
                                <FontAwesomeIcon icon="chart-line" className="me-2" />
                                Actividad de Asignaciones
                              </h6>
                            </Card.Header>
                            <Card.Body className="p-3">
                              {assignments.length > 0 ? (
                                <>
                                  <div className="mb-3">
                                    <small className="text-muted text-uppercase fw-semibold fs-11">
                                      Asignaciones Recientes
                                    </small>
                                  </div>
                                  
                                  <div className="assignment-history-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {assignments.slice(0, 8).map((assignment, index) => (
                                      <div key={assignment.id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                                        <div className="d-flex align-items-center">
                                          <div className={`bg-${index % 2 === 0 ? 'info' : 'warning'} rounded-circle me-2`} style={{ width: '8px', height: '8px' }}></div>
                                          <div>
                                            <div className="fs-11 fw-semibold text-truncate" style={{ maxWidth: '150px' }}>
                                              {assignment.description}
                                            </div>
                                            <small className="text-muted">
                                              {assignment.investmentType} • {formatCurrency(assignment.dailyAmount)}/día
                                            </small>
                                          </div>
                                        </div>
                                        <div className="text-end">
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleShowAssignmentHistory(assignment);
                                            }}
                                            title="Ver historial"
                                          >
                                            <FontAwesomeIcon icon="history" className="fs-11" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {assignments.length > 8 && (
                                    <div className="text-center mt-3">
                                      <small className="text-muted">
                                        y {assignments.length - 8} asignaciones más...
                                      </small>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-center py-4">
                                  <FontAwesomeIcon icon="info-circle" className="fs-1 text-300 mb-2" />
                                  <div className="text-muted fs-11">
                                    No hay asignaciones para mostrar historial
                                  </div>
                                  <Button 
                                    variant="outline-success" 
                                    size="sm"
                                    className="mt-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAssignmentActiveTab('new-assignment');
                                    }}
                                  >
                                    <FontAwesomeIcon icon="plus" className="me-1" />
                                    Crear Primera Asignación
                                  </Button>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                      
                      {/* Información adicional de auditoría */}
                      <Row className="mt-4">
                        <Col xs={12}>
                          <Card className="border-info">
                            <Card.Header className="bg-light py-2">
                              <h6 className="mb-0 text-info">
                                <FontAwesomeIcon icon="info-circle" className="me-2" />
                                Información de Auditoría y Trazabilidad
                              </h6>
                            </Card.Header>
                            <Card.Body className="p-3">
                              <Row>
                                <Col md={3}>
                                  <div className="text-center p-2">
                                    <div className="bg-primary rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                      <FontAwesomeIcon icon="file-alt" className="text-white fs-7" />
                                    </div>
                                    <div className="fs-11 fw-semibold">Orden</div>
                                    <div className="fs-11 text-muted">Creada: {selectedOrder.fechaCreacion ? formatDateSafe(selectedOrder.fechaCreacion) : 'N/A'}</div>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="mt-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleShowOrderHistory(selectedOrder);
                                      }}
                                    >
                                      <FontAwesomeIcon icon="history" className="me-1" />
                                      Historial
                                    </Button>
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="text-center p-2">
                                    <div className="bg-success rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                      <FontAwesomeIcon icon="list" className="text-white fs-7" />
                                    </div>
                                    <div className="fs-11 fw-semibold">Asignaciones</div>
                                    <div className="fs-11 text-muted">Total: {assignments.length}</div>
                                    <div className="fs-11 text-success fw-semibold">
                                      {formatCurrency(assignments.reduce((sum, a) => {
                                        let amount = parseFloat(a.montoValor || a.amount) || 0;
                                        const isPercentage = a.amountType === 'percentage' || 
                                                           (!a.amountType && amount <= 100 && amount > 0);
                                        if (isPercentage && selectedOrder?.inversionNeta) {
                                          amount = (amount * parseFloat(selectedOrder.inversionNeta)) / 100;
                                        }
                                        return sum + amount;
                                      }, 0))}
                                    </div>
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="text-center p-2">
                                    <div className="bg-warning rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                      <FontAwesomeIcon icon="edit" className="text-white fs-7" />
                                    </div>
                                    <div className="fs-11 fw-semibold">Modificaciones</div>
                                    <div className="fs-11 text-muted">Última: {selectedOrder.fechaModificacion ? formatDateSafe(selectedOrder.fechaModificacion) : 'N/A'}</div>
                                    <div className="fs-11 text-muted">Ver historial completo para detalles</div>
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="text-center p-2">
                                    <div className="bg-info rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                      <FontAwesomeIcon icon="clock" className="text-white fs-7" />
                                    </div>
                                    <div className="fs-11 fw-semibold">Estado</div>
                                    <div className="fs-11">
                                      <Badge bg={selectedOrder.status === 'Activa' ? 'success' : 'secondary'}>
                                        {selectedOrder.status || 'Activa'}
                                      </Badge>
                                    </div>
                                    <div className="fs-11 text-muted">Sistema automático</div>
                                  </div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Card.Body>
          </Card>
        )}

        {/* Modal de Fee Admin */}
        <AdminFeeModal
          show={showAdminFeeModal}
          onHide={handleCloseAdminFeeModal}
          onSelectFee={handleSelectAdminFee}
          clientId={window.wizardContext?.orderData?.clienteId || formData.clienteId}
          orderStartDate={window.wizardContext?.orderData?.fechaDesde || formData.fechaDesde}
          orderEndDate={window.wizardContext?.orderData?.fechaHasta || formData.fechaHasta}
          grossInvestment={parseFloat(window.wizardContext?.orderData?.inversionBruta || formData.inversionBruta) || null}
        />

        {/* Wizard de Nueva Orden */}
        <OrderWizardProvider>
          <OrderWizardLayout
            show={showNewOrderModal}
            onHide={handleCloseModal}
            onSubmit={handleWizardSubmit}
            editingOrder={editingOrder}
            clients={clients}
            businessUnits={businessUnits}
            availableAccounts={availableAccounts}
            loadingClients={loadingClients}
            loadingAccounts={loadingAccounts}
            onClientChange={handleWizardClientChange}
            onOpenAdminFeeModal={handleWizardOpenAdminFee}
          />
        </OrderWizardProvider>

        {/* Modal de Confirmación de Eliminación */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
              Confirmar Eliminación
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Estás seguro de que deseas eliminar esta orden?</p>
            {orderToDelete && (
              <div className="bg-light p-3 rounded">
                <strong>ID:</strong> {orderToDelete.id}<br/>
                <strong>Cliente:</strong> {orderToDelete.cliente}<br/>
                <strong>Descripción:</strong> {orderToDelete.descripcion}
              </div>
            )}
            <p className="text-danger mt-3 mb-0">
              <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
              Esta acción no se puede deshacer.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              <FontAwesomeIcon icon="times" className="me-1" />
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Eliminando...</span>
                  </div>
                  Eliminando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="trash" className="me-1" />
                  Eliminar Orden
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Confirmación de Eliminación de Asignación */}
        <Modal show={showDeleteAssignmentModal} onHide={() => setShowDeleteAssignmentModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
              Confirmar Eliminación de Asignación
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Estás seguro de que deseas eliminar esta asignación de presupuesto?</p>
            {assignmentToDelete && (
              <div className="bg-light p-3 rounded">
                <strong>ID:</strong> {assignmentToDelete.id}<br/>
                <strong>Descripción:</strong> {assignmentToDelete.description}<br/>
                <strong>Monto:</strong> {formatCurrency(assignmentToDelete.amount)}<br/>
                <strong>Tipo:</strong> {assignmentToDelete.investmentType}
              </div>
            )}
            <p className="text-danger mt-3 mb-0">
              <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
              Esta acción no se puede deshacer y la asignación será eliminada permanentemente.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowDeleteAssignmentModal(false)}
              disabled={deletingAssignment}
            >
              <FontAwesomeIcon icon="times" className="me-1" />
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDeleteAssignment}
              disabled={deletingAssignment}
            >
              {deletingAssignment ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Eliminando...</span>
                  </div>
                  Eliminando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="trash" className="me-1" />
                  Eliminar Asignación
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Cálculo Presupuestario */}
        <BudgetCalculationModal
          show={showCalculationModal}
          onHide={handleCloseCalculationModal}
          assignment={selectedCalculationAssignment}
          distributionInfo={calculationDistributionInfo}
        />

        {/* Modal de Historial */}
        <HistoryModal
          show={showHistoryModal}
          onHide={handleCloseHistoryModal}
          entityType={historyEntityType}
          entityId={historyEntityId}
          entityName={historyEntityName}
        />
      </div>
    </Container>
  );
};

export default OrderInsertPage; 